import { apiFetch } from '../lib/api';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NETWORK_UNAVAILABLE, type BonusView } from '../lib/bonus-state';
import type { BonusRefreshReason } from '../lib/bonus-api';
import { BonusPromoBanner } from './BonusPromoBanner';
import { BonusWelcome } from './BonusWelcome';
import { BONUS_AVAILABLE_IN_BUILD } from '../lib/bonus-availability';
import { useTonConnectUI } from '@tonconnect/ui-react';
import { track, consumeFirstFlipFlag } from '../lib/track';
import { DepositModal } from './DepositModal';
import { BoostTierSelector } from './BoostTierSelector';
import type { BalanceState } from '../App';
import type { BoostTier, TierConfigPayload, TierConfigEntry } from '../lib/boost-tier-config';
import {
    deriveCtaCopy,
    deriveBaseSummary,
    fmtTon,
    computeGrossCostNano,
    canAffordTier,
    findTierEntry,
} from '../lib/ui-derivers';
import { deriveDemoSettle, demoBalanceDeltaNano } from '../lib/demo-settle';
// CF-HEADLINE-BALANCE-V1: the headline reads the SAME derived surface the wallet drawer
// reads. It does not re-derive chips locally — two writers on one money figure is how
// the drawer and the headline would drift apart.
import type { BalanceSurface } from '../lib/balance-surface';
import { availableGrossNano, chipsAreFunding } from '../lib/balance-surface';
import {
    emptyDemoTelemetryState,
    accumulateDemoFlip,
    shouldFlushByThreshold,
    shouldFlushOnHide,
    buildDemoBeacon,
    type DemoTelemetryState,
} from '../lib/demo-telemetry';
// CF-ANALYTICS-FOUNDATION-V1 Ph-2: per-event client beacon (the ML grain). recordEvent stamps +
// batches an identity-free AnalyticsEvent; the app-wide shell (events-beacon.ts) flushes it. SESSION_ID
// is the per-app-open session shared with the App-shell lifecycle events.
import { recordEvent, SESSION_ID } from '../lib/events-beacon';
import { isBalanceFresh } from '../lib/balance-freshness';
import { parseTonAmount, formatTonExact } from '../lib/ton-format';
import { deriveCoinflipMode } from '../lib/coinflip-mode';
import { DEFAULT_BET_TON, ladderCappedBy, snapBetIntoLadder } from '../lib/bet-ladder';
import { useT } from '../i18n';
import { isDragonTiger, isGerman, isBrazil } from '../lib/app-variant';
// CF-BRAND-LION-DIAMOND-V1 (PL #11686/#11733): bundled brand marks replace the
// platform 🦁/💎 emoji on the default faces — device emoji fonts differ per OS
// (Android's lion reads soft), bundled art renders identically everywhere.
import cfLionMark from '../assets/brand/cf-lion.svg';
import cfDiamondMark from '../assets/brand/cf-diamond.svg';

// CF-DEPOSIT-UX-CRITICAL-V2: Auto-retry helper for /flip-request when backend
// returns 503 with body.error === 'hot_wallet_balance_unavailable'. Up to 2 retries
// at 1500ms spacing. MUST reuse the same body (caller stringifies ONCE) across
// attempts so flip_request_id + commit_id + choice + stake_nano + client_seed_hex
// + boost_tier stay byte-identical. NEVER re-calls /next-commit between retries
// (that would invalidate the commit_id binding). NEVER retries on fetch/catch/
// network/parse failure, 409, 402, other 4xx, maintenance_mode,
// hot_wallet_unconfigured, temporary_insolvency_protection, or any non-503 status.
const FLIP_REQUEST_503_RETRY_MAX = 2;
const FLIP_REQUEST_503_RETRY_DELAY_MS = 1500;

async function flipRequestWithRetry(
    apiBase: string,
    bodyJson: string,
): Promise<{ resp: Response; body: unknown }> {
    for (let attempt = 0; attempt <= FLIP_REQUEST_503_RETRY_MAX; attempt++) {
        const resp = await apiFetch(`${apiBase}/api/v1/flip-request`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: bodyJson,
        });
        const parsed: unknown = await resp.json().catch(() => ({}));
        const is503HotWalletBalance =
            resp.status === 503 &&
            parsed != null &&
            typeof parsed === 'object' &&
            'error' in parsed &&
            (parsed as { error: unknown }).error === 'hot_wallet_balance_unavailable';
        if (!is503HotWalletBalance || attempt === FLIP_REQUEST_503_RETRY_MAX) {
            return { resp, body: parsed };
        }
        await new Promise<void>((r) => setTimeout(r, FLIP_REQUEST_503_RETRY_DELAY_MS));
    }
    throw new Error('flipRequestWithRetry: unreachable');
}

interface CoinFlipProps {
    wallet: any;
    walletAddress?: string;
    rawAddress?: string;
    // CF-DEMO-BALANCE-FLASH-FIX-V1: true once TonConnect restore resolves; while
    // false the surface is RESTORING (neutral — never demo, never real-CTA).
    connectionRestored?: boolean;
    balance?: string | null;                 // Wallet TON balance (informational)
    // Discriminated union per R6 Binding 3. CoinFlip gates the entire money
    // surface on `balanceState.kind`; the loose-field props below are
    // projections of the `loaded` arm kept for legacy callers in this
    // component's body.
    balanceState?: BalanceState;
    depositBalance?: string | null;          // From balanceState.loaded only
    maxBetNano?: string | null;              // From balanceState.loaded only
    balanceLoaded?: boolean;                 // === (balanceState.kind === 'loaded')
    // CF-REMOVE-REDSCREEN-WALLS-V1: bounded action-freshness gate. `balanceFresh`
    // (loaded AND within the freshness budget) gates the real-money flip; display is
    // never gated on it. `balanceLoadedAtMs` powers the synchronous submit-time hard
    // guard in handleFlip (closes the ≤5s ticker-lag window). Both are null/false in
    // demo / loading / error.
    balanceFresh?: boolean;
    /** CF-HEADLINE-BALANCE-V1: canonical cash+chips view, computed once in App.tsx. */
    balanceSurface?: BalanceSurface;
    balanceLoadedAtMs?: number | null;
    // CF-V11-CTA-MORPH-V1 (R4 SIGN binding 9): tier_config projection of the
    // `loaded` arm. Required for real-money tier-aware CTA + affordability;
    // null in demo / loading / error states. Fail-closed — never coerce to a
    // local-constant fallback in real mode.
    tierConfig?: TierConfigPayload | null;
    // Tier selection state — owned by App.tsx so a /compact-style remount
    // doesn't silently reset to Standard mid-game.
    selectedTier: BoostTier;
    setSelectedTier: (tier: BoostTier) => void;
    isTelegram?: boolean;
    showDeposit?: boolean;
    setShowDeposit?: (open: boolean) => void;
    showInfo?: boolean;
    setShowInfo?: (open: boolean) => void;
    onConnect: () => void;
    onDisconnect?: () => void;
    apiBase: string;
    onVerify?: (flipRequestId: string) => void;
    onBalanceRefresh?: () => void;
    // CF-BONUS Ticket B: relayed from App, which is the single owner. CoinFlip neither fetches
    // nor caches bonus state — it passes the view to DepositModal and reports the one event
    // App cannot see from here (a failed flip's refund).
    bonusView?: BonusView;
    onBonusRefresh?: (reason: BonusRefreshReason) => void;
    onDepositRefresh?: () => void;
    // CF-WITHDRAW-BUTTON-REINSTATE-V1 (PL #6879/#6884): opens the WithdrawModal
    // via App.tsx's descriptor snapshot. App owns withdrawDescriptor state
    // (CF-FRONTEND-WITHDRAW-STATE-LATCH-FIX-V1); CoinFlip only signals intent.
    // Undefined in demo / non-real modes — guarded at the call site.
    onOpenWithdraw?: () => void;
}

type Choice = 'heads' | 'tails';
// CF-DEMO-FLIP-ANIMATION-FIX: demo flips settle synchronously in-browser (no
// network round-trip), so they need a synthetic neutral-spin windup before the
// landing — this both YIELDS so React mounts the spinning coin AND gives demo the
// same flip feel real flips get from network latency. 600ms restores the
// earlier fixed neutral-windup floor that CF-FLIP-REVEAL-HANG-FIX-V1 removed.
const DEMO_NEUTRAL_WINDUP_MS = 600;

// CF-FLIP-PREFETCH-COMMIT-V1: a commit prefetched at choice-time is consumed at the
// FLIP tap only while comfortably within the backend's 60s ISSUED expiry
// (seed-commitments.ts ISSUED_EXPIRY_SECONDS). A cached commit older than this is
// discarded and the tap falls through to the inline fetch. 15s margin under 60s —
// enough to absorb the response RTT + normal device clock skew (fetchedAtMs is the
// client clock, stamped after the response, so it slightly UNDER-counts true server
// age). If skew ever still lapses a cached commit, flip-request returns 409
// commit_invalid and the inline recovery handles it (the ref is cleared on consume,
// so the re-tap fetches a fresh commit — no loop, no stuck state).
const PREFETCH_MAX_AGE_MS = 45000;

type FlipState = 'idle' | 'flipping' | 'result';

// CF-HIGHROLLER-STAKES-V1: the canonical chip ladder (0.5/2/8/18/38/50), its
// default, the backend-cap filter, and the "selected stake is always a rendered
// chip" invariant (Scorer 1502 R2) live in the pure ../lib/bet-ladder module so
// the invariant is unit-tested without pulling React/i18n into the test program.

// Web Audio sound engine — zero external files.
function createSoundEngine() {
    let ctx: AudioContext | null = null;

    function getCtx() {
        if (!ctx) ctx = new AudioContext();
        return ctx;
    }

    function tone(freq: number, duration: number, type: OscillatorType = 'sine', vol = 0.15) {
        const c = getCtx();
        const osc = c.createOscillator();
        const gain = c.createGain();
        osc.type = type;
        osc.frequency.value = freq;
        gain.gain.value = vol;
        gain.gain.exponentialRampToValueAtTime(0.001, c.currentTime + duration);
        osc.connect(gain);
        gain.connect(c.destination);
        osc.start();
        osc.stop(c.currentTime + duration);
    }

    return {
        flip: () => {
            // CF-FLIP-VISUAL-RESPONSE-V1: tick count reduced 8 → 5 so the
            // tick starts complete within 800ms (last tick fires at i=4 with
            // i*200ms delay; each tone lasts 50ms). Sequence ends within the
            // typical network-RTT window — no audio "tail" persisting past
            // the visual outcome reveal.
            for (let i = 0; i < 5; i++) {
                setTimeout(() => tone(800 + i * 50, 0.05, 'square', 0.08), i * 200);
            }
        },
        win: () => {
            tone(523, 0.15, 'sine', 0.2);
            setTimeout(() => tone(659, 0.15, 'sine', 0.2), 120);
            setTimeout(() => tone(784, 0.15, 'sine', 0.2), 240);
            setTimeout(() => tone(1047, 0.3, 'sine', 0.25), 380);
        },
        lose: () => {
            tone(400, 0.2, 'sine', 0.12);
            setTimeout(() => tone(350, 0.25, 'sine', 0.1), 180);
        },
        click: () => tone(600, 0.05, 'sine', 0.1),
        select: () => {
            tone(500, 0.08, 'sine', 0.12);
            setTimeout(() => tone(700, 0.1, 'sine', 0.12), 60);
        },
    };
}

const sound = createSoundEngine();

function haptic(type: string = 'medium') {
    try { (window as any).Telegram?.WebApp?.HapticFeedback?.impactOccurred(type); } catch {}
}
function hapticNotify(type: string = 'success') {
    try { (window as any).Telegram?.WebApp?.HapticFeedback?.notificationOccurred(type); } catch {}
}

interface Particle {
    x: number; y: number; vx: number; vy: number;
    radius: number; color: string; rotation: number; vr: number;
    life: number;
}

function ConfettiCanvas({ active }: { active: boolean }) {
    const canvasRef = React.useRef<HTMLCanvasElement>(null);
    const particles = React.useRef<Particle[]>([]);
    const animRef = React.useRef<number>(0);

    useEffect(() => {
        if (!active || !canvasRef.current) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d')!;
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;

        const colors = ['#f4d56f', '#10b981', '#3b82f6', '#f43f5e', '#a855f7', '#fbbf24'];

        particles.current = Array.from({ length: 60 }, () => ({
            x: canvas.width / 2 + (Math.random() - 0.5) * 100,
            y: canvas.height * 0.35,
            vx: (Math.random() - 0.5) * 12,
            vy: -Math.random() * 14 - 4,
            radius: Math.random() * 6 + 3,
            color: colors[Math.floor(Math.random() * colors.length)],
            rotation: Math.random() * 360,
            vr: (Math.random() - 0.5) * 15,
            life: 1,
        }));

        function animate() {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            let alive = false;

            for (const p of particles.current) {
                if (p.life <= 0) continue;
                alive = true;
                p.x += p.vx;
                p.y += p.vy;
                p.vy += 0.35;
                p.vx *= 0.99;
                p.rotation += p.vr;
                p.life -= 0.012;

                ctx.save();
                ctx.translate(p.x, p.y);
                ctx.rotate((p.rotation * Math.PI) / 180);
                ctx.globalAlpha = Math.max(0, p.life);
                ctx.fillStyle = p.color;
                ctx.fillRect(-p.radius / 2, -p.radius / 2, p.radius, p.radius * 0.6);
                ctx.restore();
            }

            if (alive) animRef.current = requestAnimationFrame(animate);
        }

        animRef.current = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(animRef.current);
    }, [active]);

    if (!active) return null;
    return <canvas ref={canvasRef} className="fixed inset-0 pointer-events-none z-50" />;
}

// CF-WITHDRAW-DISPLAY-PRECISION-V1: the local 2-decimal `formatTonBalance` truncation
// was removed. The custodial balance (a validated bigint) formats via the canonical
// EXACT `formatTonExact`; the informational on-chain fallback uses fail-loud
// `parseTonAmount` (lib/ton-format.ts).

function parseBigIntSafe(s: string | null | undefined): bigint | null {
    if (s == null) return null;
    try { return BigInt(s); } catch { return null; }
}

function bytesToHex(bytes: Uint8Array): string {
    return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
}

// FlipRequestResponse from /api/v1/flip-request — discriminated union per
// the SETTLED / replayed_settled / FAILED / in_flight / commit_invalid axis.
// CF-V11-CTA-MORPH-V1: settled + replayed_settled responses now echo the
// applied tier metadata so the UI displays the authoritative gross + payout
// (Scorer R2 correction #6 — response shape echo).
type FlipRequestResponse =
    | {
          status: 'settled';
          flip_request_id: string;
          flip_id: string;
          outcome: 0 | 1;
          won: boolean;
          hmac_hex: string;
          new_balance_nano: string;
          boost_tier: BoostTier;
          fee_nano: string;
          multiplier_bps: number;
          gross_cost_nano: string;
          payout_nano: string;
      }
    | {
          status: 'replayed_settled';
          outcome: 0 | 1;
          won: boolean;
          hmac_hex: string;
          flip_id: string;
          boost_tier: BoostTier;
          fee_nano: string;
          multiplier_bps: number;
          gross_cost_nano: string;
          payout_nano: string;
      }
    | { status: 'FAILED' | 'FAILED_REFUNDED' }
    | { status: 'in_flight' }
    | { error: 'commit_invalid'; detail?: string }
    | { error: string; detail?: string };

// Snapshot of tier math at result render time. Real flips populate from the
// response; demo flips populate from the local Standard math. Either way the
// "You Won" / loss displays read these directly so a tier-switch during the
// flip animation cannot mutate the displayed payout.
interface ResultTierSnapshot {
    tier: BoostTier;
    stakeNano: bigint;
    multBps: number;
    feeNano: bigint;
    grossNano: bigint;
    payoutNano: bigint;
}

interface NextCommitResponse {
    commit_id: string;
    commit_hash_hex: string;
    chain_id: number;
    seed_index: number;
    reused: boolean;
    expires_in_seconds: number;
}

export function CoinFlip({
    wallet, walletAddress, balance, balanceState, depositBalance, maxBetNano, balanceLoaded,
    balanceFresh, balanceLoadedAtMs, balanceSurface,
    tierConfig, selectedTier, setSelectedTier,
    showDeposit: showDepositProp, setShowDeposit: setShowDepositProp,
    showInfo: showInfoProp, setShowInfo: setShowInfoProp,
    onConnect, apiBase, onVerify, onDepositRefresh, onOpenWithdraw, bonusView, onBonusRefresh,
    connectionRestored,
}: CoinFlipProps) {
    const t = useT();
    // CF-BONUS-WOW-V1: one shared truth for "is the first-deposit offer live for this
    // player" — the same server-confirmed predicate the banner/welcome components apply
    // internally, lifted here so the host can also SUPPRESS the redundant old
    // wallet-connected card (PL #12717: two identical stacked Deposit CTAs). Build-flag
    // included so excluded build variants keep the old card exactly as before.
    const bonusOfferView = bonusView ?? NETWORK_UNAVAILABLE;
    const bonusOfferLive = BONUS_AVAILABLE_IN_BUILD && bonusOfferView.kind === 'none' && bonusOfferView.offerAvailable;
    const [tonConnectUI] = useTonConnectUI();
    const [showDepositLocal, setShowDepositLocal] = useState(false);
    const showDeposit = showDepositProp !== undefined ? showDepositProp : showDepositLocal;
    const setShowDeposit = setShowDepositProp || setShowDepositLocal;
    const [choice, setChoice] = useState<Choice | null>(null);
    // CF-HIGHROLLER-STAKES-V1 R2: default MUST be a ladder member (was `1`, which
    // is not in the 0.5/2/8/18/38/50 ladder → a hidden stake with no visible chip).
    const [betAmount, setBetAmount] = useState(DEFAULT_BET_TON);
    const [flipState, setFlipState] = useState<FlipState>('idle');
    const [result, setResult] = useState<{
        outcome: Choice;
        won: boolean;
        flipRequestId?: string;
        tierSnapshot: ResultTierSnapshot;
        isFirstFlip?: boolean;
        // CF-FLIP-BALANCE-IMMEDIATE-V1: exact post-settle custodial balance (nano)
        // from the `settled` flip response. Carried on the latch, consumed ONLY by the
        // post-reveal effect (never applied before reveal — leak-safe). Absent on
        // replayed_settled / demo.
        newBalanceNano?: string;
    } | null>(null);
    const [showConfetti, setShowConfetti] = useState(false);
    const [coinClass, setCoinClass] = useState('');
    // CF-FLIP-BALANCE-IMMEDIATE-V1 (PL #7601 pt2): render-only optimistic balance for
    // the balance row when the main game UI resumes post-reveal, so the settled balance
    // appears instantly instead of lagging the GET /api/v1/balance refresh by ~1s.
    // NEVER feeds gating/affordability/freshness/chip-adjust (those keep reading the
    // authoritative depositNano/balanceNumeric). 'pending' = stake/gross debited at
    // flip-live; 'final' = exact post-settle balance applied at reveal. Cleared when the
    // authoritative depositBalance prop catches up (or when an aborted flip returns to idle).
    const [optimisticBal, setOptimisticBal] = useState<{ kind: 'pending' | 'final'; mode: 'real' | 'demo'; display: string; sourceDeposit?: string } | null>(null);
    // CF-FLIP-OUTCOME-LEAK-FIX-V1: the prior `pendingTierSnapshot` (payout shown
    // during the spin) was removed — surfacing the settled payout mid-flip leaked
    // the outcome (0 TON ⇒ loss). Payout now appears ONLY on the result screen.
    const [demoBalance, setDemoBalance] = useState(100);
    const [demoFlipCount, setDemoFlipCount] = useState(0);
    // CF-FIRST-FLIP-ACTIVATION-V1: armed by the one-tap demo hero. The flip itself is
    // dispatched from an effect once the randomly-chosen side commits to `choice`, so
    // handleFlip (the untouched money-path fn) reads the fresh side. Demo-only.
    const [autoFlipPending, setAutoFlipPending] = useState(false);
    const [showInfoLocal, setShowInfoLocal] = useState(false);
    const showInfo = showInfoProp !== undefined ? showInfoProp : showInfoLocal;
    const setShowInfo = setShowInfoProp || setShowInfoLocal;
    const [toast, setToast] = useState<string | null>(null);

    // CF-FLIP-VISUAL-RESPONSE-V1: ref to the flipping coin element so the reveal
    // can fire on `animationend`. mountedRef gates state mutations so unmount
    // mid-flip never writes to a dead component.
    const coinRef = useRef<HTMLDivElement>(null);
    const mountedRef = useRef(true);

    // CF-DEMO-TELEMETRY-V1: batched per-user demo-play beacon. We accumulate demo-flip count +
    // session seconds locally and flush a single {demoFlips, sessionSecs} POST /api/v1/demo-flip
    // (auth via initData; identity derived server-side — NEVER in the body) after a small
    // count/time threshold and on visibilitychange/pagehide. Fire-and-forget (.catch) — telemetry
    // must never throw into demo settlement. Counters reset AT dispatch (lose telemetry over
    // double-count; no retry). Real-money flips NEVER touch this. NOT Plausible.
    const demoTelemetryRef = useRef<DemoTelemetryState>(emptyDemoTelemetryState());
    const lastDemoFlipMsRef = useRef<number | null>(null);
    // CF-ANALYTICS-FOUNDATION-V1 Ph-2: consecutive same-result run for the demo_flip_settled event
    // (streak_len / streak_dir). { dir:'win'|'loss', len } — reset/extended each demo settle. Null
    // until the first demo flip. A ref (not state) — it never renders, so it adds no re-render.
    const demoStreakRef = useRef<{ dir: 'win' | 'loss'; len: number } | null>(null);
    // Monotonic 1-based index of the demo flip within this app-open session (flip_index_in_session).
    const demoFlipIndexRef = useRef(0);
    const dispatchDemoTelemetry = useCallback(() => {
        const beacon = buildDemoBeacon(demoTelemetryRef.current);
        if (!beacon) return;
        // Snapshot-then-reset BEFORE the network call so a flip during the in-flight request
        // accumulates fresh (no double-count, no loss of the new flip).
        demoTelemetryRef.current = emptyDemoTelemetryState();
        void apiFetch(`${apiBase}/api/v1/demo-flip`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(beacon),
        }).catch((err) => {
            // Best-effort: drop the deltas (already reset) rather than retry. Never surface to play.
            console.warn('demo telemetry beacon failed', err);
        });
    }, [apiBase]);

    // CF-FLIP-REVEAL-HANG-FIX-V1 (PL #7184): make the POST-SETTLE reveal impossible
    // to strand on "Flipping...". The prior design awaited `animationend` OR a single
    // 2300ms timer — a backgrounded webview (phone lock / app-switch) suspends BOTH,
    // with no resume path, so once the outcome was known the result screen could hang
    // forever even though the flip had already settled (funds safe; UI never advanced).
    // Fix: latch the known outcome, then route every reveal trigger — animationend,
    // the hard-timer backstop, and a visibility-resume — through ONE idempotent
    // commitReveal. First caller wins; the rest are no-ops.
    // KNOWN RESIDUAL (tracked → CF-FLIP-PRESETTLE-RESUME-V1): a webview frozen DURING
    // the pre-settle network round-trip (before the latch is set) is NOT covered here.
    // Safe recovery there needs an idempotent re-poll of flip_request_id, never a
    // naive retry (a new flip_request_id could double-flip). Funds stay safe in that
    // window; the client may need a reopen until the follow-up lands.
    const flipInFlightRef = useRef(false);
    const pendingRevealRef = useRef<{
        outcome: Choice;
        won: boolean;
        flipRequestId?: string;
        tierSnapshot: ResultTierSnapshot;
        isFirstFlip?: boolean;
        newBalanceNano?: string;
    } | null>(null);
    const revealTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // CF-FLIP-PREFETCH-COMMIT-V1: hold a provably-fair commit fetched in the
    // background at choice-time so the real flip pays only the settle round-trip at
    // the tap (matching the demo's snappiness). Refs (not state) — the commit is
    // never rendered, so this adds no re-render and no handleFlip dep churn. The
    // commit is server_seed-only (no player input); prefetching it earlier changes
    // WHEN it is fetched, never the fairness ordering (commit_hash_hex is still
    // published before the client_seed is generated at the tap).
    const prefetchedCommitRef = useRef<{ commit: NextCommitResponse; fetchedAtMs: number } | null>(null);
    const prefetchInFlightRef = useRef(false);
    const prefetchAbortRef = useRef<AbortController | null>(null);

    const commitReveal = useCallback(() => {
        const pending = pendingRevealRef.current;
        if (!pending) return;                 // already committed — idempotent latch
        pendingRevealRef.current = null;
        if (revealTimerRef.current !== null) {
            clearTimeout(revealTimerRef.current);
            revealTimerRef.current = null;
        }
        if (!mountedRef.current) return;      // dead component — never write state
        setResult(pending);
        setFlipState('result');
    }, []);

    useEffect(() => () => {
        mountedRef.current = false;
        if (revealTimerRef.current !== null) clearTimeout(revealTimerRef.current);
    }, []);

    // CF-DEMO-TELEMETRY-V1: flush any pending demo-play batch when the app is backgrounded or
    // closed (visibilitychange→hidden / pagehide), and once more on unmount, so a short demo
    // session below the count/time threshold still reports. Fire-and-forget inside
    // dispatchDemoTelemetry; the no-op guard (buildDemoBeacon→null) makes an empty flush free.
    useEffect(() => {
        const onHide = () => {
            if (typeof document !== 'undefined' && document.visibilityState !== 'hidden') return;
            if (shouldFlushOnHide(demoTelemetryRef.current)) dispatchDemoTelemetry();
        };
        const onPageHide = () => {
            if (shouldFlushOnHide(demoTelemetryRef.current)) dispatchDemoTelemetry();
        };
        document.addEventListener('visibilitychange', onHide);
        window.addEventListener('pagehide', onPageHide);
        return () => {
            document.removeEventListener('visibilitychange', onHide);
            window.removeEventListener('pagehide', onPageHide);
            // Final best-effort flush on unmount (component teardown / navigation).
            if (shouldFlushOnHide(demoTelemetryRef.current)) dispatchDemoTelemetry();
        };
    }, [dispatchDemoTelemetry]);

    // Keep the synchronous in-flight guard in lockstep with flipState: a flip is
    // "in flight" only while actively flipping. Every error path already returns to
    // 'idle' and success advances to 'result', so this single effect both arms and
    // releases the guard without having to touch the ~10 early-return sites.
    useEffect(() => {
        flipInFlightRef.current = (flipState === 'flipping');
    }, [flipState]);

    // CF-FLIP-REVEAL-HANG-FIX-V1: if the tab was hidden during a pending reveal
    // (outcome known, result not yet shown), commit on return to the foreground.
    // visibilitychange fires only on real hidden<->visible transitions, so a plain
    // window focus while already visible can NOT force an early reveal (leak-safe).
    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === 'visible' && pendingRevealRef.current) {
                commitReveal();
            }
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, [commitReveal]);

    const showToast = (msg: string) => {
        setToast(msg);
        setTimeout(() => setToast(prev => (prev === msg ? null : prev)), 6000);
    };

    const isConnected = !!wallet && !!walletAddress;
    const depositNano = parseBigIntSafe(depositBalance);
    const hasDeposit = !!balanceLoaded && depositNano !== null && depositNano > 0n;
    // Real mode predicate (R4 §4 — custodial): connected + balance loaded +
    // depositBalance > 0. Demo mode covers every other case (disconnected,
    // pre-load, connected-but-zero-balance).
    // CF-DEMO-BALANCE-FLASH-FIX-V1 (Scorer design-align 1208Z): three-state mode.
    // RESTORING (TonConnect restore unresolved) is neither demo nor real — it
    // suppresses the demo balance / Practice banner / demo CTA / enabled flip so a
    // returning real-money player never sees the fake "100 CREDITS" flash. demo is
    // entered ONLY after restore proves the wallet state (never as a fallback).
    const mode = deriveCoinflipMode(!!connectionRestored, isConnected, hasDeposit);
    const restoring = mode === 'restoring';
    const isReal = mode === 'real';
    const inDemoMode = mode === 'demo';
    // CF-FIRST-FLIP-ACTIVATION-V1: the first demo flip of the session (none done yet)
    // is the one-tap hero — heads/tails cards + Boost/Mega tiers are hidden to remove
    // the two-step commit + choice overload that churns cold ad-acquired users. Always
    // false in real mode, so the real-money surface is unaffected.
    const firstDemoFlip = inDemoMode && demoFlipCount === 0;
    const displayCurrency = inDemoMode ? t('coinflip.currency.credits') : t('common.ton_unit');

    // CF-DEMO-BALANCE-FLASH-FIX-V1 R2 (Scorer 1229Z): RESTORING is non-numeric at
    // the derivation layer — the demo 100 never feeds balanceNumeric / chip
    // affordability / the auto-adjust effect while restore is unresolved.
    const tonDisplay = restoring
        ? '…'
        : isConnected
        ? (balanceLoaded
            ? (depositNano !== null
                ? formatTonExact(depositNano)
                : (() => { const r = balance ? parseTonAmount(balance) : null; return r && r.ok ? r.ton : '...'; })())
            : '...')
        : demoBalance.toFixed(2);
    const activeCurrencyBalance = tonDisplay;
    // CF-FLIP-BALANCE-IMMEDIATE-V1: the balance ROW shows the optimistic value when
    // present and its mode matches the active mode; everything else (gating,
    // affordability, freshness, chip auto-adjust) keeps reading tonDisplay/balanceNumeric
    // /depositNano. This derivation is the ONLY render consumer of optimisticBal.
    const balanceRowDisplay =
        optimisticBal && ((optimisticBal.mode === 'real' && isReal) || (optimisticBal.mode === 'demo' && inDemoMode))
            ? optimisticBal.display
            : tonDisplay;

    // CF-HEADLINE-BALANCE-V1: chips are shown on the headline ONLY when the shared surface
    // says there is a real amount to show. 'establishing' / 'unavailable' / 'hidden' all
    // render nothing rather than a zero or a placeholder — a money figure invented to fill
    // a slot is worse than an absent one. Real mode only; demo has no chips.
    const chipsNano: bigint | null =
        isReal && balanceSurface && balanceSurface.chips.kind === 'amount' && balanceSurface.chips.nano > 0n
            ? balanceSurface.chips.nano
            : null;

    // ⛔ PL #13704/#13705 SUPERSEDES #13428's no-sum ruling FOR THIS ROW, and he sent the mock:
    // one figure `10.00 TON`, with `Withdraw 5.00` on the button beside it.
    //
    // ⭐ Why the sum is honest HERE and was not before. Chips are spent before cash, so 5 chips +
    // 5 cash really is 10 you can bet — the total is the true PLAYABLE figure. The objection to a
    // total was never the arithmetic, it was that a lone total hides how much you can actually take
    // out. His design answers that in the same row: the withdraw button carries the cash amount.
    // Remove the amount from that button and this sum becomes dishonest again.
    const cashNanoForRow: bigint | null =
        isReal && balanceSurface && balanceSurface.cash.kind === 'known' ? balanceSurface.cash.nano : null;
    const playableRowDisplay: string | null =
        cashNanoForRow !== null && chipsNano !== null ? formatTonExact(cashNanoForRow + chipsNano) : null;
    // CF-AFFORDABILITY-V1 — what the LEDGER would fund, not what cash alone covers.
    // Chips burn first (bonus-engine.ts:484-485); only the cash remainder is checked
    // (ledger.ts:199). Gating on cash alone made every chip-holder falsely restricted.
    // ⛔ Falls back to depositNano when the surface is absent, so a missing prop can only ever
    //    NARROW affordability, never widen it.
    const affordableGrossNano: bigint | null =
        isReal && balanceSurface
            ? availableGrossNano(balanceSurface.cash, balanceSurface.chips)
            : depositNano;
    // Suppression predicate for the optimistic row: only when chips are CONFIRMED funding.
    const chipFunded: boolean = isReal && !!balanceSurface && chipsAreFunding(balanceSurface.chips);
    // ⛔ LATCHED AT SUBMIT, never recomputed later (Scorer 18:05Z blocker 1). After
    //    refreshBonus('flip_settled') the live chips figure has already moved, so a late read
    //    would answer a DIFFERENT question than the one the flip was funded under. The suppression
    //    decision belongs to the flip, not to the current render.
    const chipFundedAtSubmitRef = useRef<boolean>(false);

    const withdrawableDisplay: string | null =
        cashNanoForRow !== null ? formatTonExact(cashNanoForRow) : null;

    // Max bet in TON. In a real session this MUST come from a parsed maxBetNano;
    // if the env-driven value is missing/unparsable we fail closed to 0 so the
    // chip ladder collapses to its smallest entry and the Flip button gates on
    // canSubmitBet. Pre-load and demo contexts use Infinity (no real money path).
    const maxBetTon = (() => {
        if (!isConnected) return Infinity;
        if (!balanceLoaded) return Infinity;
        if (!hasDeposit) return Infinity;
        if (!maxBetNano) {
            console.error('🚨 maxBetNano missing in live session — failing closed to 0', { depositBalance });
            return 0;
        }
        try {
            return Number(BigInt(maxBetNano)) / 1e9;
        } catch (err: any) {
            console.error('🚨 maxBetNano parse failed — failing closed to 0', { maxBetNano, err: err?.message || String(err) });
            return 0;
        }
    })();

    const betAmounts = ladderCappedBy(maxBetTon);

    // Keep the selected stake inside the visible+affordable ladder. Snaps DOWN when
    // the cap/balance shrinks below it AND when it is not a rendered chip at all
    // (CF-HIGHROLLER-STAKES-V1 R2 — no hidden stake outside the visible chips).
    // Never masks a no-affordable-chip state behind a fake selection: snap returns
    // the value unchanged when nothing is affordable, and canSubmitBet then gates.
    useEffect(() => {
        // CF-DEMO-BALANCE-FLASH-FIX-V1 R2: inert while restore unresolved — never
        // auto-adjust the bet from the demo 100 during RESTORING.
        if (restoring) return;
        const bal = parseFloat(activeCurrencyBalance);
        if (isNaN(bal) || bal <= 0) return;
        const next = snapBetIntoLadder(betAmount, betAmounts, bal, maxBetTon);
        if (next !== betAmount) setBetAmount(next);
    }, [restoring, activeCurrencyBalance, depositBalance, balance, maxBetTon]);

    // CF-DEMO-BALANCE-FLASH-FIX-V1 R2: explicit fail-closed to 0 under RESTORING
    // (do not let the demo 100 / parseFloat fallback bless unknown wallet state).
    const balanceNumeric = restoring ? 0 : (() => { const b = parseFloat(activeCurrencyBalance); return isNaN(b) ? 0 : b; })();

    // ═══ CF-V11-CTA-MORPH-V1: tier-aware affordability ═══
    // Real-money path uses BigInt nano gross-cost gate per Scorer R3 #4. The
    // tier_config payload is the single source of truth for fee_bps + mult_bps
    // (no local constants — Scorer R4 SIGN binding 9). Demo path keeps the
    // float `balanceNumeric` check; tier UI is hidden in demo so selectedTier
    // is always logically Standard there.
    const stakeNano = BigInt(Math.round(betAmount * 1e9));
    const tierEntryReal: TierConfigEntry | null =
        isReal && tierConfig ? findTierEntry(tierConfig, selectedTier) : null;
    const grossNanoReal: bigint = tierEntryReal
        ? computeGrossCostNano(stakeNano, tierEntryReal.fee_bps)
        : stakeNano;
    const grossTonDisplay: number = Number(grossNanoReal) / 1e9;
    // CF-DEMO-TIERS-V1: demo path resolves the SAME tier_config (no local
    // constants — Scorer R4 binding 9). When tier_config is present (Telegram-authed
    // user, even at balance 0) the demo flip applies the real tier gross-cost + payout;
    // when absent (disconnected) tierEntryDemo is null → Standard demo (unchanged).
    const tierEntryDemo: TierConfigEntry | null =
        inDemoMode && tierConfig ? findTierEntry(tierConfig, selectedTier) : null;
    const grossNanoDemo: bigint = deriveDemoSettle(stakeNano, tierEntryDemo).grossNano;
    const grossTonDemo: number = Number(grossNanoDemo) / 1e9;
    const canAffordReal: boolean = isReal
        ? tierEntryReal !== null && affordableGrossNano !== null && canAffordTier(affordableGrossNano, stakeNano, tierEntryReal)
        : tierEntryDemo !== null
            ? canAffordTier(BigInt(Math.round(balanceNumeric * 1e9)), stakeNano, tierEntryDemo)
            : betAmount <= balanceNumeric;
    // CF-REMOVE-REDSCREEN-WALLS-V1: real-money flips also require a bounded-fresh
    // balance (Scorer constraint 5). Demo path is unaffected — no real money, no
    // loadedAtMs, so freshness does not gate practice flips.
    // CF-DEMO-BALANCE-FLASH-FIX-V1: no enabled flip while TonConnect restore is
    // unresolved (RESTORING). balanceNumeric falls back to the demo 100 pre-restore,
    // so the `!restoring` guard is the fail-closed lever (Scorer design-align (b)/(c)).
    const canSubmitBet = !restoring && betAmount > 0 && betAmount <= maxBetTon && canAffordReal && (!isReal || !!balanceFresh);

    const choiceToNum = (c: Choice): 0 | 1 => (c === 'heads' ? 0 : 1);
    const numToChoice = (n: 0 | 1): Choice => (n === 0 ? 'heads' : 'tails');

    // CF-ANALYTICS-FOUNDATION-V1 Ph-2: demo_tier_selected — capture Boost/Mega intent in demo. The
    // mode here is DERIVED (there is no demo↔real toggle button), so we observe the selectedTier
    // change while inDemoMode rather than a click handler. multiplier_bps/fee_bps come ONLY from the
    // backend tier_config entry (never hardcoded). The initial 'standard' default does NOT fire (we
    // skip the mount value); a fee-bearing tier change does. No identity — server resolves subject_id.
    const prevDemoTierRef = useRef<BoostTier | null>(null);
    useEffect(() => {
        if (!inDemoMode) { prevDemoTierRef.current = null; return; }
        const prev = prevDemoTierRef.current;
        prevDemoTierRef.current = selectedTier;
        if (prev === null || prev === selectedTier) return; // skip mount + no-op re-renders
        recordEvent({
            event_type: 'demo_tier_selected',
            session_id: SESSION_ID,
            mode: 'demo',
            tier: selectedTier,
            properties: {
                tier: selectedTier,
                multiplier_bps: tierEntryDemo ? tierEntryDemo.multiplier_bps : null,
                fee_bps: tierEntryDemo ? tierEntryDemo.fee_bps : null,
            },
        });
    }, [inDemoMode, selectedTier, tierEntryDemo]);

    // CF-ANALYTICS-FOUNDATION-V1 Ph-2: mode_switched — the demo↔real surface transition (mode is
    // derived from wallet/deposit state, so we observe the transition of `mode`). Skips the initial
    // mount value and the transient 'restoring' state (only demo↔real transitions are meaningful).
    const prevModeRef = useRef<typeof mode | null>(null);
    useEffect(() => {
        const prev = prevModeRef.current;
        // Only track stable demo/real states; never treat 'restoring' as a from/to endpoint.
        if (mode === 'restoring') return;
        prevModeRef.current = mode;
        if (prev === null || prev === 'restoring' || prev === mode) return;
        recordEvent({
            event_type: 'mode_switched',
            session_id: SESSION_ID,
            mode,
            properties: { from: prev, to: mode },
        });
    }, [mode]);

    // CF-FLIP-PREFETCH-COMMIT-V1: fetch the next provably-fair commit in the
    // background so the FLIP tap pays only the settle round-trip. Bails unless real
    // mode; reuses a cached/in-flight commit (heads/tails toggling never refetches),
    // so at most ONE /next-commit per flip cycle — rate-limit exposure unchanged from
    // the old fetch-at-tap path. A prefetch HTTP/network failure is non-fatal and is
    // NOT a silent fund fallback: the tap path re-fetches inline and surfaces every
    // error LOUDLY (503 / chain_exhausted / !ok toasts).
    const prefetchCommit = useCallback(() => {
        if (!isReal) return;
        if (prefetchInFlightRef.current) return;
        if (prefetchedCommitRef.current) return;
        prefetchInFlightRef.current = true;
        const ctrl = new AbortController();
        prefetchAbortRef.current = ctrl;
        (async () => {
            try {
                const resp = await apiFetch(`${apiBase}/api/v1/next-commit`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({}),
                    signal: ctrl.signal,
                });
                if (!resp.ok) return; // inline fetch at tap will surface the error loudly
                const commit = await resp.json() as NextCommitResponse;
                if (!ctrl.signal.aborted) {
                    prefetchedCommitRef.current = { commit, fetchedAtMs: Date.now() };
                }
            } catch (err: any) {
                if (err?.name !== 'AbortError') {
                    console.error('prefetch_commit_failed (non-fatal — inline fetch runs at tap)', err);
                }
            } finally {
                prefetchInFlightRef.current = false;
            }
        })();
    }, [isReal, apiBase]);

    // Pre-warm a commit whenever a real-money player has picked a side and the
    // surface is idle + balance-fresh — covers the first pick AND "Choose Again"
    // (idle with a retained choice). Demo never prefetches (prefetchCommit bails on
    // !isReal). The internal guards keep this to one fetch until consumed/cleared.
    useEffect(() => {
        if (isReal && balanceFresh && flipState === 'idle' && choice) {
            prefetchCommit();
        }
    }, [isReal, balanceFresh, flipState, choice, prefetchCommit]);

    // Never consume a prefetched commit across a wallet / real-mode boundary: drop
    // the cache + abort any in-flight prefetch when identity changes (and on unmount
    // via the cleanup). Conservative — costs at most one inline fetch.
    useEffect(() => {
        return () => {
            prefetchedCommitRef.current = null;
            prefetchAbortRef.current?.abort();
        };
    }, [walletAddress, isReal]);

    const handleFlip = useCallback(async () => {
        if (!choice) return;
        // CF-FLIP-REVEAL-HANG-FIX-V1: synchronous guard — two fast taps (PL hit 3
        // flips in ~11s) can never start overlapping reveal pipelines. Released by
        // the flipState effect when this flip resolves to 'result' or 'idle'.
        if (flipInFlightRef.current) return;
        // CF-DEMO-BALANCE-FLASH-FIX-V1: never flip while TonConnect restore is
        // unresolved (the button is already disabled via canSubmitBet; fail-closed belt).
        if (restoring) return;

        if (inDemoMode && demoBalance < grossTonDemo) {
            showToast(t('coinflip.toast.not_enough_practice'));
            return;
        }

        // CF-REMOVE-REDSCREEN-WALLS-V1: synchronous action-freshness hard guard for
        // real-money flips. Re-checks at the click instant (real Date.now()) so a
        // stale snapshot can never start a real flip even if the button's disabled
        // state lagged a ticker behind. handleFlip stays free of balance side effects
        // (the 15s poll + visibility-resume re-probe own refresh) so the outcome-leak
        // invariant holds; the backend is authoritative regardless.
        if (isReal && !isBalanceFresh(balanceLoadedAtMs ?? null, Date.now())) {
            showToast(t('coinflip.toast.balance_updating'));
            return;
        }

        flipInFlightRef.current = true;
        setFlipState('flipping');
        setResult(null);
        setShowConfetti(false);
        // CF-FLIP-BALANCE-IMMEDIATE-V1: optimistic 'pending' debit at flip-live. Real
        // uses the EXACT gross (grossNanoReal incl tier fee); if depositNano or
        // tierEntryReal is unavailable we SKIP the optimistic paint (no stake-only
        // fallback — that would show a wrong debit for Boost/Mega). Demo debits the
        // stake from the local demoBalance. Display-only (the balance row isn't shown
        // during the spin); this is the value the main UI resumes to until the
        // post-reveal 'final' (real) or settled demoBalance (demo) supersedes it.
        if (isReal) {
            // ⛔ NO OPTIMISTIC OVERRIDE WHEN CHIPS FUND ANY PART. `depositNano - grossNanoReal` is
            //    cash-only arithmetic: with 5 cash and an 8 gross it renders -3, because the ledger
            //    took 5 from chips and 3 from cash. Modelling the split here would make the client
            //    a SECOND writer of burn-first arithmetic — the same two-writers defect that let the
            //    drawer and the headline drift apart. The row holds its last confirmed value until
            //    the refresh lands: a number that pauses beats a number that is wrong.
            chipFundedAtSubmitRef.current = chipFunded;
            if (!chipFunded && depositNano !== null && tierEntryReal !== null) {
                setOptimisticBal({ kind: 'pending', mode: 'real', display: formatTonExact(depositNano - grossNanoReal), sourceDeposit: depositBalance ?? undefined });
            }
        } else {
            setOptimisticBal({ kind: 'pending', mode: 'demo', display: (demoBalance - grossTonDemo).toFixed(2) });
        }
        // CF-FLIP-VISUAL-RESPONSE-V1 + CF-FLIP-DRAMATIC-REVEAL-V1: start the
        // neutral spin immediately on click (no outcome hint while we wait for
        // settle), then — once the outcome is known — swap to the dramatic
        // landing animation that ends on the real face (see the reveal block
        // below). The payout NUMBER never appears during the spin (that was the
        // CF-FLIP-OUTCOME-LEAK-FIX leak); the coin landing on its own face is
        // the game's natural reveal, not the leak.
        setCoinClass('coin-spinning-neutral');

        sound.flip();
        haptic('medium');

        let outcome: Choice;
        let won: boolean;
        let flipRequestId: string | undefined;
        // CF-FLIP-OUTCOME-LEAK-FIX-V1: captured during settle, carried on the
        // result, consumed by the result-keyed effect (analytics post-reveal).
        let isFirstFlip = false;
        // CF-V11-CTA-MORPH-V1: snapshot of tier math at submit/settle time.
        // Real flips fill from the authoritative backend response (settled +
        // replayed_settled both echo tier metadata). Demo flips fill from
        // local Standard math. Captured BEFORE setResult so a mid-animation
        // tier switch cannot retroactively alter the displayed payout.
        let tierSnapshot: ResultTierSnapshot;
        // CF-FLIP-BALANCE-IMMEDIATE-V1: exact post-settle balance from the `settled`
        // response only; carried on the latch, applied to the optimistic display ONLY
        // after reveal. replayed_settled does not echo it (left undefined → the pending
        // deduction stays until onDepositRefresh reconciles — no invented value).
        let newBalanceNano: string | undefined;

        if (isReal) {
            // ═══ CUSTODIAL FLIP — two-step commit-reveal ═══
            try {
                // 1. Obtain the commit. The backend returns the commit_id bound to
                //    our tg_user_id; commit_hash_hex is SHA-256 of the server_seed,
                //    published BEFORE the player commits any randomness (step 2). The
                //    server cannot retroactively choose a seed.
                //    CF-FLIP-PREFETCH-COMMIT-V1: consume the commit prefetched at
                //    choice-time so the first round-trip is already paid before the
                //    tap — the real flip then waits only for the settle round-trip
                //    (step 3), matching the demo's snappiness. Consume-and-clear (one
                //    commit per flip — no grinding) + abort any in-flight prefetch so
                //    it cannot repopulate the ref mid-flip. A cached commit near the
                //    backend's 60s expiry (PREFETCH_MAX_AGE_MS) is discarded and we
                //    fall through to the inline fetch — byte-identical to the
                //    pre-prefetch path, every error surfaced loudly (no silent
                //    fallback). Fairness ordering is unchanged either way: the commit
                //    is obtained here, the client_seed only in step 2.
                const cachedPrefetch = prefetchedCommitRef.current;
                prefetchedCommitRef.current = null;
                prefetchAbortRef.current?.abort();
                let commit: NextCommitResponse;
                if (cachedPrefetch && (Date.now() - cachedPrefetch.fetchedAtMs) < PREFETCH_MAX_AGE_MS) {
                    commit = cachedPrefetch.commit;
                } else {
                    const commitResp = await apiFetch(`${apiBase}/api/v1/next-commit`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({}),
                    });
                    if (!commitResp.ok) {
                        setFlipState('idle');
                        if (commitResp.status === 503) {
                            const body = await commitResp.json().catch(() => ({} as any));
                            if (body.error === 'chain_exhausted') {
                                showToast(t('coinflip.toast.chain_exhausted'));
                            } else {
                                showToast(t('coinflip.toast.service_busy'));
                            }
                        } else {
                            showToast(t('coinflip.toast.commit_failed'));
                        }
                        return;
                    }
                    commit = await commitResp.json() as NextCommitResponse;
                }

                // 2. Generate the client-side randomness:
                //    - flip_request_id: UUIDv4, the SOLE idempotency key.
                //    - client_seed_hex: 32 random bytes, mixed into the HMAC
                //      message; the player owns half the entropy.
                if (typeof crypto.randomUUID !== 'function' || typeof crypto.getRandomValues !== 'function') {
                    setFlipState('idle');
                    showToast(t('coinflip.toast.no_secure_random'));
                    return;
                }
                const flipReqId = crypto.randomUUID();
                const clientSeedBytes = new Uint8Array(32);
                crypto.getRandomValues(clientSeedBytes);
                const clientSeedHex = bytesToHex(clientSeedBytes);
                flipRequestId = flipReqId;

                // 3. Submit the flip request. Backend runs Tx A (debit + lock)
                //    + Tx B (settle) synchronously; the response carries the
                //    settled outcome OR a discriminated error.
                // CF-V11-CTA-MORPH-V1: boost_tier is required + validated
                // server-side (missing → 400 missing_boost_tier, unknown → 400
                // invalid_boost_tier). Send the locally-selected tier verbatim;
                // backend echoes back the applied tier metadata for the
                // displayed payout (defense against silent mismatch).
                const stakeNanoStr = stakeNano.toString();
                // CF-DEPOSIT-UX-CRITICAL-V2: build body ONCE; flipRequestWithRetry
                // reuses verbatim across attempts. Retry trigger is parsed HTTP 503
                // + body.error === 'hot_wallet_balance_unavailable' only.
                const flipRequestBody = JSON.stringify({
                    flip_request_id: flipReqId,
                    commit_id: commit.commit_id,
                    choice: choiceToNum(choice),
                    stake_nano: stakeNanoStr,
                    client_seed_hex: clientSeedHex,
                    boost_tier: selectedTier,
                });
                const flipResult = await flipRequestWithRetry(apiBase, flipRequestBody);
                const flipResp = flipResult.resp;
                const body = flipResult.body as FlipRequestResponse;

                if (flipResp.status === 409) {
                    setFlipState('idle');
                    if ('status' in body && body.status === 'in_flight') {
                        showToast(t('coinflip.toast.flip_in_flight'));
                    } else if ('error' in body && body.error === 'commit_invalid') {
                        showToast(t('coinflip.toast.commit_expired'));
                    } else {
                        showToast(t('coinflip.toast.request_conflict'));
                    }
                    return;
                }
                if (!flipResp.ok) {
                    setFlipState('idle');
                    if ('error' in body && body.error === 'insufficient_balance') {
                        showToast(t('coinflip.toast.insufficient_balance'));
                    } else if ('error' in body && body.error === 'stake_out_of_range') {
                        showToast(t('coinflip.toast.stake_out_of_range'));
                    } else if ('error' in body && body.error === 'maintenance_mode') {
                        showToast(t('coinflip.toast.maintenance'));
                    } else if ('error' in body && body.error === 'hot_wallet_unconfigured') {
                        showToast(t('coinflip.toast.hot_wallet_unconfigured'));
                    } else if ('error' in body && body.error === 'hot_wallet_balance_unavailable') {
                        showToast(t('coinflip.toast.hot_wallet_balance_unavailable'));
                    } else if ('error' in body && body.error === 'temporary_insolvency_protection') {
                        showToast(t('coinflip.toast.insolvency_protection'));
                    } else {
                        showToast(t('coinflip.toast.flip_failed_safe'));
                    }
                    return;
                }

                // 200 OK — parse discriminated union.
                if ('status' in body) {
                    if (body.status === 'settled') {
                        outcome = numToChoice(body.outcome);
                        won = body.won;
                        tierSnapshot = {
                            tier: body.boost_tier,
                            stakeNano,
                            multBps: body.multiplier_bps,
                            feeNano: BigInt(body.fee_nano),
                            grossNano: BigInt(body.gross_cost_nano),
                            payoutNano: BigInt(body.payout_nano),
                        };
                        // CF-FLIP-BALANCE-IMMEDIATE-V1: capture exact post-settle
                        // balance (settled only). Validated + applied post-reveal.
                        newBalanceNano = body.new_balance_nano;
                    } else if (body.status === 'replayed_settled') {
                        outcome = numToChoice(body.outcome);
                        won = body.won;
                        tierSnapshot = {
                            tier: body.boost_tier,
                            stakeNano,
                            multBps: body.multiplier_bps,
                            feeNano: BigInt(body.fee_nano),
                            grossNano: BigInt(body.gross_cost_nano),
                            payoutNano: BigInt(body.payout_nano),
                        };
                        // CF-FLIP-OUTCOME-LEAK-FIX-V1: balance refresh deferred to
                        // the post-reveal effect (no pre-reveal balance change).
                    } else if (body.status === 'FAILED' || body.status === 'FAILED_REFUNDED') {
                        setFlipState('idle');
                        showToast(body.status === 'FAILED_REFUNDED'
                            ? t('coinflip.toast.failed_refunded')
                            : t('coinflip.toast.flip_incomplete'));
                        // CF-BONUS Ticket B: this branch returns EARLY and never reaches the
                        // post-reveal effect, so onDepositRefresh does not fire and App would
                        // otherwise never learn the refund happened. A chip-funded flip's
                        // refund restores chips — the surface must re-read.
                        if (body.status === 'FAILED_REFUNDED') onBonusRefresh?.('failed_flip_refunded');
                        return;
                    } else {
                        setFlipState('idle');
                        showToast(t('coinflip.toast.unexpected_response'));
                        return;
                    }
                } else {
                    setFlipState('idle');
                    showToast(t('coinflip.toast.unexpected_response'));
                    return;
                }

                // CF-FLIP-OUTCOME-LEAK-FIX-V1: consume the first-flip flag now
                // (one-shot), but DEFER analytics + balance refresh to the
                // post-reveal effect — no outcome-dependent side effect fires
                // while flipState==='flipping'.
                isFirstFlip = consumeFirstFlipFlag();
            } catch (err: any) {
                console.error('🚨 flip_request_failed', err);
                setFlipState('idle');
                showToast(t('coinflip.toast.connection_error'));
                return;
            }
        } else {
            // ═══ DEMO MODE — client-side crypto, never burns server seeds ═══
            // CF-DEMO-TIERS-V1: the 50% coin is UNCHANGED (provably-fair parity —
            // the tier never biases the outcome). Tier only changes COST + PAYOUT,
            // applied below from the SAME tier_config the real path uses (when present;
            // disconnected demo with no tier_config stays Standard math).
            try {
                const randomBytes = new Uint8Array(32);
                crypto.getRandomValues(randomBytes);
                outcome = randomBytes[31] % 2 === 0 ? 'heads' : 'tails';
                won = outcome === choice;
            } catch {
                setFlipState('idle');
                showToast(t('coinflip.toast.something_wrong'));
                return;
            }

            // CF-FLIP-OUTCOME-LEAK-FIX-V1: demo balance/count + analytics deferred
            // to the post-reveal effect (no pre-reveal balance change).

            const demoSettle = deriveDemoSettle(stakeNano, tierEntryDemo);
            tierSnapshot = {
                tier: tierEntryDemo ? tierEntryDemo.key : 'standard',
                stakeNano,
                multBps: demoSettle.multBps,
                feeNano: demoSettle.feeNano,
                grossNano: demoSettle.grossNano,
                payoutNano: demoSettle.payoutNano,
            };

            // CF-DEMO-FLIP-ANIMATION-FIX: the demo branch settles synchronously (no
            // network round-trip), so without a yield React never commits the
            // flipState='flipping' render before the reveal check below — coinRef stays
            // null, so the `if (!coinEl)` path fires commitReveal immediately and the
            // flip reveals with NO animation. (Regression: CF-FLIP-REVEAL-HANG-FIX-V1
            // removed the prior fixed neutral-windup floor, demo's only async yield.) Restore a
            // neutral windup for demo ONLY: it (a) yields so the spinning coin mounts
            // and (b) holds the neutral spin for the same feel real flips get from
            // network latency. Reveal stays un-hangable (2300ms hard-timer + idempotent
            // commitReveal below); outcome-leak stays closed (neutral spin shows no
            // outcome); the real-money flip path is untouched.
            await new Promise<void>((resolve) => setTimeout(resolve, DEMO_NEUTRAL_WINDUP_MS));
        }

        if (!mountedRef.current) return;

        // CF-FLIP-REVEAL-HANG-FIX-V1 + restore earlier feel (PL #7147/#7150/#7184):
        // No artificial neutral-windup floor — the neutral spin lasts exactly the
        // real network round-trip (the "earlier" flip PL wanted back), then lands.
        // Swap the neutral spin for the dramatic landing on the real outcome FACE
        // (a 2.2s eased multi-turn land). This is the game's natural reveal but NOT
        // the payout number (the CF-FLIP-OUTCOME-LEAK stays closed); all win/loss
        // side effects fire AFTER reveal from the result-keyed effect, never mid-spin.
        setCoinClass(outcome === 'heads' ? 'coin-flip-heads' : 'coin-flip-tails');

        // Latch the known outcome. From here the result is GUARANTEED to surface via
        // the idempotent commitReveal — fired by animationend, the hard-timer
        // backstop, or a visibility-resume if the tab is backgrounded mid-land.
        pendingRevealRef.current = { outcome, won, flipRequestId, tierSnapshot, isFirstFlip, newBalanceNano };

        // Hidden tab: nothing visible to protect, and the webview suspends both the
        // CSS animation and JS timers — commit immediately so a backgrounded flip
        // can never strand on "Flipping...".
        if (typeof document !== 'undefined' && document.hidden) {
            commitReveal();
            return;
        }

        const coinEl = coinRef.current;
        if (!coinEl) {
            // Flipping coin not mounted (e.g. a synchronous demo settle) — reveal now.
            commitReveal();
            return;
        }
        // Foreground reveal: the coin's natural landing fires animationend; the hard
        // timer is the backstop if animationend never arrives. Both route through the
        // idempotent commitReveal (first caller wins); the visibility effect is the
        // third path for a tab backgrounded between here and the land.
        // Listener cleanup is intentional-by-construction: { once: true } self-removes
        // on fire, and the coin node unmounts on → 'result', so the unmount effect
        // clears only the hard timer; a late animationend no-ops via commitReveal's latch.
        coinEl.addEventListener('animationend', commitReveal, { once: true });
        if (revealTimerRef.current !== null) clearTimeout(revealTimerRef.current);
        revealTimerRef.current = setTimeout(commitReveal, 2300);

        // Reveal is now armed; commitReveal (animationend / hard timer / visibility
        // resume) owns the transition to the result screen. handleFlip is done.
    }, [choice, betAmount, apiBase, isReal, balanceLoadedAtMs, inDemoMode, demoBalance, selectedTier, onDepositRefresh, commitReveal, depositNano, tierEntryReal, grossNanoReal, depositBalance]);

    // CF-FLIP-OUTCOME-LEAK-FIX-V1: ALL outcome-dependent side effects fire here,
    // keyed on the committed result — AFTER the reveal renders, never during the
    // flipping suspense window. React runs effects post-paint, so "result screen
    // first, side effects after" is mechanically true. Closes the early-tell that
    // the old in-handleFlip order could leak (sound/haptic/balance/analytics a
    // frame before the result painted).
    useEffect(() => {
        if (!result || flipState !== 'result') return;
        if (isReal) {
            onDepositRefresh?.();
            // CF-FLIP-BALANCE-IMMEDIATE-V1: apply the exact post-settle balance to the
            // optimistic display NOW (at reveal — never earlier; leak-safe). The main
            // game UI resumes to this instantly instead of the stale pre-flip value, and
            // it survives even if onDepositRefresh lags or fails until a later authoritative
            // refresh lands. replayed_settled has no new_balance_nano → reconcile the pending
            // debit instead (see the !appliedFinal branch) — no invented value.
            let appliedFinal = false;
            // ⛔ `new_balance_nano` is the CASH balance. After a chip-funded flip the headline is
            //    cash + chips, so painting a cash-only figure as the final total would understate
            //    it until the bonus refresh lands — the same lie as the pending paint, one step
            //    later. App.tsx:1093-1100 refreshes both, so suppression has a real reconciler.
            if (result.newBalanceNano && !chipFundedAtSubmitRef.current) {
                const nb = parseBigIntSafe(result.newBalanceNano);
                if (nb !== null) { setOptimisticBal({ kind: 'final', mode: 'real', display: formatTonExact(nb) }); appliedFinal = true; }
            }
            if (!appliedFinal) {
                // replayed_settled (or an unparseable value): reconcile the pending debit against the
                // authoritative balance. If depositBalance already moved off the pre-flip sourceDeposit
                // it was derived from (a refresh landed DURING the flip — the [depositBalance] effect
                // ignores changes while flipping and will NOT re-fire after reveal since the value did
                // not change again), the pending debit is stale → clear it so the resumed main UI shows
                // the authoritative settled balance. If depositBalance is unchanged, keep pending until
                // onDepositRefresh lands (the [depositBalance] effect clears it then).
                setOptimisticBal(prev =>
                    (prev && prev.kind === 'pending' && prev.mode === 'real' && prev.sourceDeposit !== (depositBalance ?? undefined))
                        ? null
                        : prev);
            }
            if (result.isFirstFlip && choice) track.firstFlip(choice, betAmount);
            if (choice) track.flipResolved(choice, result.outcome, betAmount, result.won);
        } else {
            setDemoBalance(prev => {
                // CF-DEMO-TIERS-V1: tier-aware demo balance via the same pure helper the
                // test pins — debit GROSS (stake+fee), credit PAYOUT on win. Standard
                // (gross=stake, payout=2×stake) reduces to the prior ±betAmount exactly.
                const deltaTon = Number(demoBalanceDeltaNano(result.tierSnapshot, result.won)) / 1e9;
                return prev + deltaTon;
            });
            // demoBalance is the local authoritative source — clear the optimistic
            // override so the row shows the settled demoBalance.
            setOptimisticBal(null);
            setDemoFlipCount(prev => prev + 1);
            if (choice) track.practiceFlip(choice, result.won);
            // CF-DEMO-TELEMETRY-V1: accumulate this DEMO flip + the seconds since the previous demo
            // flip into the per-user batch, then flush only when a count/time threshold is met. This
            // is the demo branch (the `else` of isReal) so real-money flips NEVER feed the beacon.
            {
                const nowMs = Date.now();
                const prevMs = lastDemoFlipMsRef.current;
                const deltaSecs = prevMs !== null ? (nowMs - prevMs) / 1000 : 0;
                const msSincePrev = prevMs !== null ? Math.max(0, nowMs - prevMs) : null;
                lastDemoFlipMsRef.current = nowMs;
                demoTelemetryRef.current = accumulateDemoFlip(demoTelemetryRef.current, deltaSecs);
                if (shouldFlushByThreshold(demoTelemetryRef.current)) dispatchDemoTelemetry();

                // CF-ANALYTICS-FOUNDATION-V1 Ph-2: emit the per-event demo_flip_settled (the ML grain).
                // Typed columns come ONLY from result.tierSnapshot (tier economics sourced from the
                // backend tier_config — never hardcoded) + the choice/outcome/won of this flip; demo
                // amounts are "virtual nano" so they line up with real bets for ML. Identity is NEVER
                // here — the server resolves the opaque subject_id from the signed initData envelope.
                const snap = result.tierSnapshot;
                // streak: extend the run on a repeat result, else start a new run of length 1.
                const dir: 'win' | 'loss' = result.won ? 'win' : 'loss';
                const prevStreak = demoStreakRef.current;
                const streakLen = prevStreak && prevStreak.dir === dir ? prevStreak.len + 1 : 1;
                demoStreakRef.current = { dir, len: streakLen };
                demoFlipIndexRef.current += 1;
                // post-settle demo balance in virtual nano (pre-settle demoBalance closure + nano delta),
                // clamped at 0 so it always satisfies the non-negative nano-string column CHECK.
                const preBalNano = BigInt(Math.round(demoBalance * 1e9));
                const balanceAfterNano = preBalNano + demoBalanceDeltaNano(snap, result.won);
                const balanceAfterClamped = balanceAfterNano < 0n ? 0n : balanceAfterNano;
                if (choice) {
                    recordEvent({
                        event_type: 'demo_flip_settled',
                        session_id: SESSION_ID,
                        mode: 'demo',
                        bet_nano: snap.stakeNano.toString(),
                        side: choiceToNum(choice),
                        tier: snap.tier,
                        multiplier_bps: snap.multBps,
                        outcome: choiceToNum(result.outcome),
                        won: result.won ? 1 : 0,
                        payout_nano: result.won ? snap.payoutNano.toString() : '0',
                        balance_after_nano: balanceAfterClamped.toString(),
                        properties: {
                            streak_len: streakLen,
                            streak_dir: dir,
                            ms_since_prev_flip: msSincePrev,
                            flip_index_in_session: demoFlipIndexRef.current,
                            fee_nano: snap.feeNano.toString(),
                            gross_nano: snap.grossNano.toString(),
                        },
                    });
                }
            }
        }
        if (result.won) {
            sound.win();
            hapticNotify('success');
            setShowConfetti(true);
            const t = setTimeout(() => setShowConfetti(false), 3000);
            return () => clearTimeout(t);
        }
        sound.lose();
        hapticNotify('error');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [result]);

    // ═══ CF-FIRST-FLIP-ACTIVATION-V1: one-tap first demo flip ═══
    // Cold ad-acquired users churn before doing even one demo flip — "pick a side
    // THEN tap FLIP" + choice overload reads as friction. The FIRST demo flip of a
    // session is therefore a SINGLE tap: a side is chosen FOR the player with the
    // SAME secure RNG the real flip uses for client seeds (crypto.getRandomValues —
    // NEVER Math.random; on missing secure RNG we fail loud with the existing
    // no_secure_random toast), then the EXISTING demo settle path runs via handleFlip
    // (left byte-for-byte unchanged). Demo telemetry fires exactly as a normal demo
    // flip because the real settle path is reused. Dispatch is deferred to the effect
    // below so handleFlip reads the freshly-committed `choice`.
    const handleFirstTapFlip = useCallback(() => {
        if (flipInFlightRef.current || autoFlipPending) return;
        let randomSide: Choice;
        try {
            if (typeof crypto === 'undefined' || typeof crypto.getRandomValues !== 'function') {
                throw new Error('no_secure_random');
            }
            const sideByte = new Uint8Array(1);
            crypto.getRandomValues(sideByte);
            randomSide = sideByte[0] % 2 === 0 ? 'heads' : 'tails';
        } catch {
            // Fail-loud — same discipline as the real flip; NEVER fall back to Math.random.
            showToast(t('coinflip.toast.no_secure_random'));
            return;
        }
        haptic('light');
        setChoice(randomSide);
        setAutoFlipPending(true);
    }, [autoFlipPending, t]);

    // Dispatch the armed one-tap flip once the chosen side has committed to `choice`.
    // Demo + idle gated: if the surface resolved to real money (or a flip already
    // started) the armed trigger is dropped — a real-money flip is NEVER auto-started.
    useEffect(() => {
        if (!autoFlipPending) return;
        if (!inDemoMode || flipState !== 'idle') { setAutoFlipPending(false); return; }
        if (!choice) return;
        setAutoFlipPending(false);
        void handleFlip();
    }, [autoFlipPending, inDemoMode, choice, flipState, handleFlip]);

    // CF-FLIP-BALANCE-IMMEDIATE-V1: drop an aborted/errored flip's 'pending' debit when
    // we return to idle (every real error path + demo crypto-fail routes to 'idle'). KEEP
    // a 'final' value — it must persist on Choose Again / Done until the authoritative
    // balance catches up; clearing it here would recreate the stale-balance flash.
    useEffect(() => {
        if (flipState === 'idle') setOptimisticBal(prev => (prev?.kind === 'pending' ? null : prev));
    }, [flipState]);

    // CF-FLIP-BALANCE-IMMEDIATE-V1: reconcile — when the authoritative depositBalance prop
    // changes (the post-reveal GET /api/v1/balance landed, or any later poll), drop the
    // optimistic override so the row shows the authoritative value (identical number → no
    // flicker). IGNORE changes while 'flipping' so a mid-spin background poll can never
    // overwrite the pre-reveal deducted display (outcome-leak protection). Reading flipState
    // from the render closure is correct: this effect re-runs on the render where
    // depositBalance changed, with that render's current flipState.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (flipState !== 'flipping') setOptimisticBal(null);
    }, [depositBalance]);

    const resetFlip = () => {
        sound.click();
        haptic('light');
        setResult(null);
        setCoinClass('');
        setFlipState('idle');
    };

    const doneFlip = () => {
        sound.click();
        haptic('light');
        setResult(null);
        setCoinClass('');
        setChoice(null);
        setFlipState('idle');
    };

    const selectChoice = (c: Choice) => {
        sound.select();
        haptic('light');
        setChoice(c);
    };

    const selectAmount = (a: number) => {
        sound.click();
        haptic('light');
        setBetAmount(a);
    };

    const ToastOverlay = toast ? (
        <div className="fixed top-4 left-4 right-4 z-[200] flex justify-center"
             style={{ animation: 'fadeIn 0.2s ease-out' }}
             onClick={() => setToast(null)}>
            <div className="glass rounded-xl px-5 py-3 text-sm font-semibold text-center max-w-xs cursor-pointer"
                 style={{ color: 'var(--text-primary)', background: 'rgba(30,30,40,0.95)', border: '1px solid var(--border-subtle)' }}>
                {toast}
            </div>
        </div>
    ) : null;

    // ═══ FLIPPING STATE ═══
    if (flipState === 'flipping') {
        return (
            // CF-DEMO-TIERS-ABUNDANCE-V1 (PL #8506): under the variant, fill the
            // available height + vertically center so the coin sits mid-screen
            // instead of floating at the top over a black void. Default unchanged.
            <div className={`flex flex-col items-center justify-center ${isDragonTiger || isGerman || isBrazil ? 'min-h-[82dvh]' : 'pt-14 pb-8'}`}>
                <div style={{ perspective: '800px' }} className="mb-10">
                    <div ref={coinRef}
                         className={`w-36 h-36 relative ${coinClass}`}
                         style={{ transformStyle: 'preserve-3d' }}>
                        {/* CF-DRAGONTIGER-THEME-V1 Phase B (D1): under the variant the
                            two faces become Dragon 龙 = gold disc / Tiger 虎 = red disc
                            (.dt-face-dragon / .dt-face-tiger). The .coin-face /
                            coin-face-back classes are KEPT (the flip animation's
                            backface geometry depends on them). Default app keeps the
                            original 🦁 gold / 💎 silver faces unchanged. */}
                        {isDragonTiger ? (
                            <div className="coin-face dt-face dt-face-dragon rounded-full shadow-2xl">
                                <span className="text-6xl">{t('common.heads')}</span>
                            </div>
                        ) : isGerman ? (
                            // CF-GERMAN-VARIANT-V1: Kopf = Adler (eagle) medallion.
                            <div className="coin-face de-face de-face-kopf rounded-full shadow-2xl">
                                <span className="text-6xl">{t('common.heads')}</span>
                            </div>
                        ) : isBrazil ? (
                            // CF-BRAZIL-VARIANT-V1: Cara = allegorical liberty-profile face.
                            <div className="coin-face br-face br-face-cara rounded-full shadow-2xl">
                                <span className="text-6xl">{t('common.heads')}</span>
                            </div>
                        ) : (
                            <div className="coin-face rounded-full shadow-2xl"
                                 style={{ background: 'linear-gradient(145deg, #fcd34d 0%, #d4a017 50%, #f4d56f 100%)', boxShadow: '0 0 60px rgba(244,213,111,0.4), inset 0 2px 4px rgba(255,255,255,0.3)' }}>
                                <div className="flex flex-col items-center">
                                    <img src={cfLionMark} alt="" className="w-14 h-14 drop-shadow-lg" />
                                    <span className="text-[10px] font-black mt-1 tracking-wider" style={{ color: 'rgba(0,0,0,0.5)' }}>{t('common.heads')}</span>
                                </div>
                            </div>
                        )}
                        {isDragonTiger ? (
                            <div className="coin-face coin-face-back dt-face dt-face-tiger rounded-full shadow-2xl">
                                <span className="text-6xl">{t('common.tails')}</span>
                            </div>
                        ) : isGerman ? (
                            // CF-GERMAN-VARIANT-V1: Zahl = struck-coin number medallion.
                            <div className="coin-face coin-face-back de-face de-face-zahl rounded-full shadow-2xl">
                                <span className="text-6xl">{t('common.tails')}</span>
                            </div>
                        ) : isBrazil ? (
                            // CF-BRAZIL-VARIANT-V1: Coroa = heraldic house crown.
                            <div className="coin-face coin-face-back br-face br-face-coroa rounded-full shadow-2xl">
                                <span className="text-6xl">{t('common.tails')}</span>
                            </div>
                        ) : (
                            <div className="coin-face coin-face-back rounded-full shadow-2xl"
                                 style={{ background: 'linear-gradient(145deg, #c0c7d1 0%, #7a8494 50%, #a0aab8 100%)', boxShadow: '0 0 60px rgba(148,163,184,0.4), inset 0 2px 4px rgba(255,255,255,0.2)' }}>
                                <div className="flex flex-col items-center">
                                    <img src={cfDiamondMark} alt="" className="w-14 h-14 drop-shadow-lg" />
                                    <span className="text-[10px] font-black mt-1 tracking-wider" style={{ color: 'rgba(255,255,255,0.5)' }}>{t('common.tails')}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <p className="text-lg font-bold tracking-tight mb-1">{t('coinflip.flipping')}</p>
                {/* CF-FLIP-OUTCOME-LEAK-FIX-V1 (PL #7056): keep ONLY the player's
                    pick during the spin. The payout was removed because its
                    settled value (0 TON ⇒ loss) leaked the outcome before reveal. */}
                <div className="flex items-center justify-center">
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {choice ? t(choice === 'heads' ? 'common.heads' : 'common.tails') : ''}
                    </span>
                </div>
            </div>
        );
    }

    // ═══ RESULT STATE ═══
    if (flipState === 'result' && result) {
        return (
            // CF-DEMO-TIERS-ABUNDANCE-V1 (PL #8506): variant fills + centers the
            // result so it doesn't float at the top over a black void. Default unchanged.
            <div className={`flex flex-col items-center justify-center ${isDragonTiger || isGerman || isBrazil ? 'min-h-[82dvh]' : 'pt-8 pb-8'}`}>
                <ConfettiCanvas active={showConfetti} />

                {result.won ? (
                    <>
                        {/* CF-DRAGONTIGER-THEME-V1 Phase B (D1 — CRITICAL status
                            separation): the WIN status is ALWAYS emerald (ring +
                            glow + "You Won" text + green payout), independent of
                            which side landed. Under the variant we place the
                            landed-side themed disc (Dragon gold / Tiger red) INSIDE
                            the emerald ring so a player who picked Tiger and WON
                            sees a red Tiger disc framed by unmistakable emerald win
                            chrome — the disc color encodes the SIDE, the emerald
                            encodes the WIN. Default app keeps the 🎉 burst. */}
                        <div className="win-pop mb-5">
                            <div className="w-32 h-32 rounded-full flex items-center justify-center relative"
                                 style={{ background: 'rgba(16, 185, 129, 0.08)' }}>
                                <div className="absolute inset-0 rounded-full glow-green" />
                                <div className="absolute inset-1 rounded-full"
                                     style={{ border: '2px solid var(--emerald)' }} />
                                {isDragonTiger ? (
                                    <div className={`dt-face ${result.outcome === 'heads' ? 'dt-face-dragon' : 'dt-face-tiger'} relative z-10`}
                                         style={{ width: '78px', height: '78px' }}>
                                        <span className="text-4xl">{t(result.outcome === 'heads' ? 'common.heads' : 'common.tails')}</span>
                                    </div>
                                ) : isGerman ? (
                                    // CF-GERMAN-VARIANT-V1: landed-side medallion inside the
                                    // emerald WIN ring (disc encodes SIDE, emerald encodes WIN).
                                    <div className={`de-face ${result.outcome === 'heads' ? 'de-face-kopf' : 'de-face-zahl'} relative z-10`}
                                         style={{ width: '78px', height: '78px' }}>
                                        <span className="text-4xl">{t(result.outcome === 'heads' ? 'common.heads' : 'common.tails')}</span>
                                    </div>
                                ) : isBrazil ? (
                                    // CF-BRAZIL-VARIANT-V1: landed-side medallion inside the
                                    // emerald WIN ring (disc encodes SIDE, emerald encodes WIN).
                                    <div className={`br-face ${result.outcome === 'heads' ? 'br-face-cara' : 'br-face-coroa'} relative z-10`}
                                         style={{ width: '78px', height: '78px' }}>
                                        <span className="text-4xl">{t(result.outcome === 'heads' ? 'common.heads' : 'common.tails')}</span>
                                    </div>
                                ) : (
                                    <span className="text-6xl relative z-10">🎉</span>
                                )}
                            </div>
                        </div>

                        <h2 className="text-sm font-bold tracking-widest uppercase mb-1 count-up"
                            style={{ color: 'var(--emerald)', letterSpacing: '0.15em' }}>
                            {t('coinflip.result.you_won')}
                        </h2>
                        <p className="text-5xl font-black mb-2 count-up" style={{ animationDelay: '0.1s' }}>
                            +{fmtTon(result.tierSnapshot.payoutNano)}
                        </p>
                        <p className="text-sm font-medium count-up" style={{ animationDelay: '0.15s', color: 'var(--text-secondary)' }}>
                            {displayCurrency}
                        </p>

                        <div className="glass-gold rounded-xl px-4 py-2 mt-4 count-up" style={{ animationDelay: '0.25s' }}>
                            <span className="text-xs font-semibold" style={{ color: 'var(--gold)' }}>
                                {t('coinflip.result.win_detail', {
                                    OUTCOME: t(result.outcome === 'heads' ? 'common.heads' : 'common.tails'),
                                    betAmount,
                                    currency: displayCurrency,
                                    mult: (result.tierSnapshot.multBps / 10000).toFixed(2),
                                })}
                            </span>
                        </div>
                    </>
                ) : (
                    <>
                        {/* CF-DRAGONTIGER-THEME-V1 Phase B (D1): on a LOSS, show the
                            LANDED side's themed disc (Dragon gold / Tiger red). The
                            loss is communicated by the NEUTRAL chrome + textual
                            "you picked …" + muted loss-amount below — NOT by the disc
                            color (so a gold Dragon disc never reads as a "win" and a
                            red Tiger disc never reads as the "loss color"; the disc
                            is purely the side that landed). The win-status emerald is
                            intentionally absent here. Default app keeps the grey
                            H/T glyph (also replaces the flagged loss glyph). */}
                        <div className="win-pop mb-5">
                            {isDragonTiger ? (
                                <div className="w-28 h-28 rounded-full flex items-center justify-center"
                                     style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                                    <div className={`dt-face ${result.outcome === 'heads' ? 'dt-face-dragon' : 'dt-face-tiger'}`}
                                         style={{ width: '72px', height: '72px' }}>
                                        <span className="text-4xl">{t(result.outcome === 'heads' ? 'common.heads' : 'common.tails')}</span>
                                    </div>
                                </div>
                            ) : isGerman ? (
                                // CF-GERMAN-VARIANT-V1: landed-side medallion on neutral chrome.
                                // Loss is communicated by the neutral frame + text below, NOT by
                                // the disc color (the medallion is purely the side that landed).
                                <div className="w-28 h-28 rounded-full flex items-center justify-center"
                                     style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                                    <div className={`de-face ${result.outcome === 'heads' ? 'de-face-kopf' : 'de-face-zahl'}`}
                                         style={{ width: '72px', height: '72px' }}>
                                        <span className="text-4xl">{t(result.outcome === 'heads' ? 'common.heads' : 'common.tails')}</span>
                                    </div>
                                </div>
                            ) : isBrazil ? (
                                // CF-BRAZIL-VARIANT-V1: landed-side medallion on neutral chrome.
                                // Loss is communicated by the neutral frame + text below, NOT by
                                // the disc color (the medallion is purely the side that landed).
                                <div className="w-28 h-28 rounded-full flex items-center justify-center"
                                     style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                                    <div className={`br-face ${result.outcome === 'heads' ? 'br-face-cara' : 'br-face-coroa'}`}
                                         style={{ width: '72px', height: '72px' }}>
                                        <span className="text-4xl">{t(result.outcome === 'heads' ? 'common.heads' : 'common.tails')}</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="w-28 h-28 rounded-full flex items-center justify-center"
                                     style={{ background: 'rgba(120, 124, 138, 0.06)', border: '1px solid rgba(120, 124, 138, 0.18)' }}>
                                    <svg width="58" height="58" viewBox="0 0 64 64" aria-hidden="true">
                                        <circle cx="32" cy="32" r="21" fill="#23262e" stroke="#474b57" strokeWidth="2.5" />
                                        <circle cx="32" cy="32" r="14.5" fill="none" stroke="#383c46" strokeWidth="1.8" />
                                        <text x="32" y="38.5" textAnchor="middle" fontSize="17" fontWeight="800" fill="#7d8492" fontFamily="inherit">{result.outcome === 'heads' ? 'H' : 'T'}</text>
                                    </svg>
                                </div>
                            )}
                        </div>

                        <h2 className="text-xl font-bold tracking-tight mb-1">
                            {t(result.outcome === 'heads' ? 'common.heads' : 'common.tails')}
                        </h2>
                        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                            {t('coinflip.result.you_picked', { choice: t(choice === 'heads' ? 'common.heads' : 'common.tails') })}
                        </p>
                        <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
                            {t('coinflip.result.loss_amount', { gross: fmtTon(result.tierSnapshot.grossNano), currency: displayCurrency })}
                        </p>
                    </>
                )}

                <p className="text-[11px] mt-6" style={{ color: 'var(--text-muted)' }}>
                    {t('coinflip.provably_fair_tagline')}
                </p>

                <div className="flex gap-3 mt-3 w-full max-w-xs">
                    <button onClick={resetFlip}
                            className="btn-primary flex-1 py-3.5 rounded-2xl text-sm tracking-wide">
                        {t('coinflip.choose_again_btn')}
                    </button>
                    {!inDemoMode && result.flipRequestId && onVerify && (
                        <button onClick={() => onVerify(result.flipRequestId!)}
                                className="glass px-5 py-3.5 rounded-2xl text-xs font-semibold transition-all active:scale-95"
                                style={{ color: 'var(--text-secondary)' }}>
                            {t('coinflip.verify_btn')}
                        </button>
                    )}
                </div>

                <button onClick={doneFlip}
                        className="mt-5 text-xs text-center transition-colors active:opacity-70"
                        style={{ color: 'var(--text-muted)' }}>
                    {t('common.done_btn')}
                </button>
            </div>
        );
    }

    // ═══ NEUTRAL SKELETON — loading OR not-yet-trustworthy balance ═══
    // CF-REMOVE-REDSCREEN-WALLS-V1 (PL #7138): the red balance-error wall is removed.
    // When connected but the balance is not a fully-parsed `loaded` snapshot —
    // `loading` (first fetch / wallet change) OR `error` (schema / auth / DTO /
    // non-retainable HTTP that cleared to neutral) — hold the neutral skeleton and
    // show NO flip / deposit / withdraw CTA. We never fall open into the money UI on
    // a balance we can't trust, and we never paint a red wall. Transport blips do NOT
    // reach here: App retains the prior `loaded` snapshot and keeps showing the game
    // (the freshness gate withholds actions if that snapshot ages out).
    if (isConnected && (!balanceState || balanceState.kind === 'loading' || balanceState.kind === 'error')) {
        return (
            <div className="pb-4">
                {ToastOverlay}
                <div className="glass rounded-2xl p-5 mb-5 max-w-md mx-auto">
                    <div className="shimmer rounded-xl mb-4" style={{ height: '40px' }} aria-hidden="true" />
                    <div className="h-px mb-4" style={{ background: 'var(--border-subtle)' }} />
                    <div className="grid grid-cols-5 gap-1.5 mb-5" aria-hidden="true">
                        {[0, 1, 2, 3, 4].map(i => (
                            <div key={`chip-skel-${i}`}
                                 className="shimmer rounded-full"
                                 style={{ height: '26px' }} />
                        ))}
                    </div>
                    <div className="shimmer rounded-xl" style={{ height: '48px' }} aria-hidden="true" />
                </div>
                <p className="text-center text-[11px]"
                   style={{ color: 'var(--text-muted)' }}
                   role="status" aria-live="polite">
                    {t('coinflip.loading_session')}
                </p>
            </div>
        );
    }

    // ═══ MAIN GAME UI ═══
    return (
        <div className="pb-4">
            {ToastOverlay}

            {/* Balance Row */}
            <div className={`flex items-center gap-2 ${inDemoMode ? 'mb-1.5' : 'mb-4'}`}>
                <div className="flex-1 flex items-center justify-between glass rounded-xl px-3.5 py-2">
                    <div className="flex items-center gap-2">
                        {restoring ? (
                            <span className="text-[13px] font-semibold" style={{ color: 'var(--text-muted)' }}>{t('coinflip.connecting')}</span>
                        ) : (
                            <>
                                {isReal ? <span className="text-sm">🎮</span> : <img src={cfDiamondMark} alt="" className="w-5 h-5" />}
                                {/* CF-HEADLINE-BALANCE-V1 R2 — PL's own mock (#13705): one figure,
                                    the playable total, with the withdrawable cash named on the
                                    button beside it. See the derivation above for why this sum is
                                    honest and a bare total would not be. */}
                                <span className="text-[13px] font-black" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                    {playableRowDisplay ?? balanceRowDisplay} <span className="text-[10px] font-semibold" style={{ color: 'var(--text-muted)' }}>{displayCurrency}</span>
                                </span>
                            </>
                        )}
                    </div>
                    {(restoring || !isConnected) ? (
                        null
                    ) : !hasDeposit ? (
                        <button
                            onClick={() => setShowDeposit(true)}
                            className="text-[10px] font-bold px-2.5 py-1 rounded-lg transition-all active:scale-95"
                            style={{ background: 'rgba(244, 213, 111, 0.1)', color: 'var(--gold)', border: '1px solid rgba(244, 213, 111, 0.2)' }}
                        >
                            {t('coinflip.deposit_to_play_btn')}
                        </button>
                    ) : (
                        // CF-WITHDRAW-BUTTON-REINSTATE-V1 (PL #6879/#6884 2026-05-28):
                        // Withdraw button restored next to "+ Add" per PL request.
                        // Muted/neutral style so "+ Add" stays the visual primary
                        // (re-funding emphasized over cash-out on the play surface).
                        // The state-ownership latch class fixed in CF-FRONTEND-
                        // WITHDRAW-STATE-LATCH-FIX-V1 is NOT reintroduced: this button
                        // calls onOpenWithdraw, which lets App.tsx snapshot
                        // {balanceNano,address} into the withdrawDescriptor (the
                        // descriptor render-guard is descriptor-truthiness only, not
                        // ANDed with live balance). Guarded on onOpenWithdraw so the
                        // button is absent in demo / non-real modes.
                        <div className="flex items-center gap-1.5">
                            {onOpenWithdraw && balanceFresh && (
                                <button
                                    onClick={() => onOpenWithdraw()}
                                    className="text-[10px] font-bold px-2.5 py-1 rounded-lg transition-all active:scale-95"
                                    style={{ background: 'rgba(136, 146, 168, 0.1)', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)' }}
                                >
                                    {t('coinflip.withdraw_btn')}{withdrawableDisplay !== null ? ` ${withdrawableDisplay}` : ''}
                                </button>
                            )}
                            <button
                                onClick={() => setShowDeposit(true)}
                                className="text-[10px] font-bold px-2.5 py-1 rounded-lg transition-all active:scale-95"
                                style={{ background: 'rgba(244, 213, 111, 0.1)', color: 'var(--gold)', border: '1px solid rgba(244, 213, 111, 0.2)' }}
                            >
                                {t('coinflip.add_funds_btn')}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Practice mode banner */}
            {inDemoMode && (
                <div className="text-center mb-4">
                    <span className="text-[9px] font-bold tracking-[0.12em] uppercase px-2.5 py-1 rounded-full inline-flex items-center gap-1"
                          style={{
                              background: 'rgba(244, 213, 111, 0.08)',
                              color: 'var(--gold)',
                              border: '1px solid rgba(244, 213, 111, 0.25)',
                          }}>
                        {t('coinflip.practice_mode_banner')}
                    </span>
                </div>
            )}

            {/* CF-BONUS-VISIBILITY-V2: first-deposit offer, demo/disconnected surface.
                Renders ONLY when the server confirmed `none` + offer_available (gate lives
                inside BonusPromoBanner); NETWORK_UNAVAILABLE default keeps an absent view
                advertising nothing. CTA is the CONNECT flow — action-honest label. */}
            {!restoring && !isConnected && (
                <BonusPromoBanner
                    view={bonusOfferView}
                    primaryLabel={t('coinflip.connect_wallet_deposit_btn')}
                    onPrimary={onConnect}
                    termsHref="/bonus-terms"
                />
            )}

            {/* Demo CTA after 5 flips */}
            {inDemoMode && demoFlipCount >= 5 && (
                <div className="not-prose my-4 rounded-2xl p-4 text-center transition-all"
                     style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.08), rgba(244,213,111,0.08))', border: '1px solid rgba(16,185,129,0.2)' }}>
                    <p className="text-[13px] font-bold" style={{ color: 'var(--gold)' }}>
                        {t('coinflip.demo_upsell_title')}
                    </p>
                    <p className="text-[11px] mt-1 mb-3" style={{ color: 'var(--text-secondary)' }}>
                        {t('coinflip.demo_upsell_sub')}
                    </p>
                    <button
                        onClick={onConnect}
                        className="btn-primary px-6 py-2.5 rounded-xl text-[13px] font-bold"
                    >
                        {t('coinflip.connect_wallet_deposit_btn')}
                    </button>
                </div>
            )}

            {/* CF-BONUS-VISIBILITY-V2: first-deposit offer, connected/no-deposit surface.
                Same server-confirmed gate as above; CTA opens the deposit modal, whose own
                BonusTeaser restates the offer at the point of commitment. */}
            {!restoring && isConnected && !hasDeposit && (
                <BonusPromoBanner
                    view={bonusOfferView}
                    primaryLabel={t('coinflip.deposit_ton_btn')}
                    onPrimary={() => setShowDeposit(true)}
                    termsHref="/bonus-terms"
                />
            )}

            {/* Connected but no deposit — suppressed while the bonus offer surface is live
                for this player (CF-BONUS-WOW-V1): the banner above carries the same CTA, and
                two identical stacked Deposit buttons deadened both (PL #12717). Ineligible
                and bonus-active players keep this card exactly as before. */}
            {!restoring && isConnected && !hasDeposit && !bonusOfferLive && (
                <div className="not-prose my-3 rounded-2xl p-3 text-center"
                     style={{ background: 'linear-gradient(135deg, rgba(244,213,111,0.06), rgba(16,185,129,0.06))', border: '1px solid rgba(244,213,111,0.15)' }}>
                    <p className="text-[12px] font-bold" style={{ color: 'var(--gold)' }}>
                        {t('coinflip.wallet_connected_banner')}
                    </p>
                    <button
                        onClick={() => setShowDeposit(true)}
                        className="btn-primary px-5 py-2 rounded-xl text-[12px] font-bold mt-2"
                    >
                        {t('coinflip.deposit_ton_btn')}
                    </button>
                    <p className="text-[8px] mt-1.5" style={{ color: 'var(--text-muted)' }}>
                        {t('coinflip.keep_demo_hint')}
                    </p>
                </div>
            )}

            {/* CF-FIRST-FLIP-ACTIVATION-V1: the first demo flip of the session is ONE
                TAP — a single prominent hero (the coin AND a big primary button are both
                tappable). Heads/Tails cards, the stake ladder, the Boost/Mega tiers and
                the standard pick-then-flip button are hidden to kill the two-step commit
                + choice overload. After the first flip the normal flow is revealed with a
                "Now pick your side" cue. Real-money mode keeps firstDemoFlip === false. */}
            {firstDemoFlip ? (
                <div className="flex flex-col items-center gap-5 pt-2 pb-3">
                    <button
                        onClick={handleFirstTapFlip}
                        disabled={autoFlipPending}
                        aria-label={t('coinflip.tap_to_flip_aria')}
                        className="active:scale-95 transition-transform"
                        style={{ perspective: '800px', borderRadius: '9999px' }}
                    >
                        {isDragonTiger ? (
                            <div className="w-32 h-32 rounded-full flex items-center justify-center dt-face dt-face-dragon shadow-2xl">
                                <span className="text-6xl">{t('common.heads')}</span>
                            </div>
                        ) : isGerman ? (
                            <div className="w-32 h-32 rounded-full flex items-center justify-center de-face de-face-kopf shadow-2xl">
                                <span className="text-6xl">{t('common.heads')}</span>
                            </div>
                        ) : isBrazil ? (
                            <div className="w-32 h-32 rounded-full flex items-center justify-center br-face br-face-cara shadow-2xl">
                                <span className="text-6xl">{t('common.heads')}</span>
                            </div>
                        ) : (
                            <div className="w-32 h-32 rounded-full flex items-center justify-center shadow-2xl"
                                 style={{ background: 'linear-gradient(145deg, #fcd34d 0%, #d4a017 50%, #f4d56f 100%)', boxShadow: '0 0 60px rgba(244,213,111,0.4), inset 0 2px 4px rgba(255,255,255,0.3)' }}>
                                <span className="text-6xl drop-shadow-lg">🪙</span>
                            </div>
                        )}
                    </button>
                    <button
                        onClick={handleFirstTapFlip}
                        disabled={autoFlipPending}
                        className="btn-primary w-full rounded-2xl text-base font-black tracking-wider"
                        style={{
                            paddingTop: '18px',
                            paddingBottom: '18px',
                            fontSize: '16px',
                            letterSpacing: '0.08em',
                            boxShadow: '0 8px 32px rgba(244, 213, 111, 0.3), 0 0 0 1px rgba(244, 213, 111, 0.1)',
                        }}
                    >
                        {t('coinflip.tap_to_flip_hero')}
                    </button>
                </div>
            ) : (
            <>
            {/* CF-FIRST-FLIP-ACTIVATION-V1: gentle cue once the one-tap first flip is
                done — the normal pick-then-flip cards are now revealed (demo only). */}
            {inDemoMode && demoFlipCount >= 1 && (
                <div className="text-center mb-2">
                    <span className="text-[12px] font-semibold" style={{ color: 'var(--text-secondary)' }}>
                        {t('coinflip.now_pick_side')}
                    </span>
                </div>
            )}

            {/* CF-DEMO-TIERS-FLIP-FIT-V1 (PL #8539/#8541/#8547): the variant-only
                "选择龙或虎" pick-side prompt was removed — the big 龙/虎 discs are
                self-evident, and dropping it (plus hiding the duplicate base-summary
                line below) lets the abundant column fit so the FLIP CTA clears the nav
                with NO pin, NO shrinking, and the choice/tier cards kept at full
                identical size (135px parity preserved — PL #8546/#8547).
                Default app was already prompt-less (isDragonTiger false) → unchanged. */}

            {/* CF-V11-CTA-MORPH-V1: 2x2 grid parity with BOOST/MEGA (PL #5869). The
                VS divider is dropped; HEADS/TAILS sit in a 2-col grid identical
                in size + treatment to the tier cards below. */}
            <div className="grid grid-cols-2 gap-2 mb-3">
                <button
                    onClick={() => selectChoice('heads')}
                    className={`choice-card rounded-2xl py-3.5 flex flex-col items-center justify-center gap-1.5 relative ${choice === 'heads' ? 'selected' : ''}`}
                    aria-pressed={choice === 'heads'}
                    aria-label={t('coinflip.pick_heads_aria')}
                >
                    {choice === 'heads' && (
                        <span
                            className="absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-black"
                            style={{
                                background: 'linear-gradient(180deg, var(--gold-light), var(--gold-dark))',
                                color: '#1c1917',
                                boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
                            }}
                            aria-hidden="true"
                        >
                            ✓
                        </span>
                    )}
                    {/* CF-DRAGONTIGER-THEME-V1 Phase B (D1): Dragon 龙 gold disc under
                        the variant; default app keeps the 🦁 face. The themed disc
                        always shows its side color (Dragon=gold) — selection is
                        signaled by the .choice-card.selected ring + the ✓ badge
                        above, never by recoloring the disc. */}
                    {isDragonTiger ? (
                        <div className="w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 dt-face dt-face-dragon">
                            <span className="text-4xl">{t('common.heads')}</span>
                        </div>
                    ) : isGerman ? (
                        // CF-GERMAN-VARIANT-V1: Kopf = Adler medallion. Constant side
                        // color; selection reads via the .choice-card.selected ring + ✓.
                        <div className="w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 de-face de-face-kopf">
                            <span className="text-4xl">{t('common.heads')}</span>
                        </div>
                    ) : isBrazil ? (
                        // CF-BRAZIL-VARIANT-V1: Cara = allegorical liberty-profile face. Constant side
                        // color; selection reads via the .choice-card.selected ring + ✓.
                        <div className="w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 br-face br-face-cara">
                            <span className="text-4xl">{t('common.heads')}</span>
                        </div>
                    ) : (
                        <div className="w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300"
                             style={{
                                 background: choice === 'heads'
                                     ? 'linear-gradient(145deg, #fcd34d 0%, #d4a017 100%)'
                                     : 'var(--bg-elevated)',
                                 boxShadow: choice === 'heads' ? '0 8px 30px rgba(244,213,111,0.25)' : 'none',
                             }}>
                            <img src={cfLionMark} alt="" className="w-16 h-16" />
                        </div>
                    )}
                </button>

                <button
                    onClick={() => selectChoice('tails')}
                    className={`choice-card rounded-2xl py-3.5 flex flex-col items-center justify-center gap-1.5 relative ${choice === 'tails' ? 'selected' : ''}`}
                    aria-pressed={choice === 'tails'}
                    aria-label={t('coinflip.pick_tails_aria')}
                >
                    {choice === 'tails' && (
                        <span
                            className="absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-black"
                            style={{
                                background: 'linear-gradient(180deg, var(--gold-light), var(--gold-dark))',
                                color: '#1c1917',
                                boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
                            }}
                            aria-hidden="true"
                        >
                            ✓
                        </span>
                    )}
                    {/* CF-DRAGONTIGER-THEME-V1 Phase B (D1): Tiger 虎 red disc under
                        the variant; default app keeps the 💎 face. The themed disc
                        always shows its side color (Tiger=red) — selection is
                        signaled by the .choice-card.selected ring + the ✓ badge
                        above, never by recoloring the disc. */}
                    {isDragonTiger ? (
                        <div className="w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 dt-face dt-face-tiger">
                            <span className="text-4xl">{t('common.tails')}</span>
                        </div>
                    ) : isGerman ? (
                        // CF-GERMAN-VARIANT-V1: Zahl = struck-coin number medallion.
                        <div className="w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 de-face de-face-zahl">
                            <span className="text-4xl">{t('common.tails')}</span>
                        </div>
                    ) : isBrazil ? (
                        // CF-BRAZIL-VARIANT-V1: Coroa = heraldic house crown.
                        <div className="w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 br-face br-face-coroa">
                            <span className="text-4xl">{t('common.tails')}</span>
                        </div>
                    ) : (
                        <div className="w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300"
                             style={{
                                 background: choice === 'tails'
                                     ? 'linear-gradient(145deg, #c0c7d1 0%, #7a8494 100%)'
                                     : 'var(--bg-elevated)',
                                 boxShadow: choice === 'tails' ? '0 8px 30px rgba(148,163,184,0.2)' : 'none',
                             }}>
                            <img src={cfDiamondMark} alt="" className="w-16 h-16" />
                        </div>
                    )}
                </button>
            </div>

            {/* CF-V11-CTA-MORPH-V1: chip ladder — smaller round pills (PL #5869).
                Per-chip affordability now uses GROSS cost (stake + selected-tier
                fee) so a chip that the backend would reject under the active
                tier cannot show as enabled (Scorer R3 #4). */}
            <div className="mb-3">
                {/* CF-DEMO-TIERS-ABUNDANCE-V1 (PL #8511): under the 金龙虎 variant the
                    stake chips render on ONE row (equal-width flex) instead of the
                    default 5-col grid that wrapped the 6-chip demo ladder to 5+1.
                    Default app keeps the grid. */}
                <div className={`gap-1.5 ${isDragonTiger || isGerman || isBrazil ? 'flex' : 'grid grid-cols-5'}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {betAmounts.map(amount => {
                        const chipStakeNano = BigInt(Math.round(amount * 1e9));
                        const houseCanCover = amount <= maxBetTon;
                        const canAffordChip = (() => {
                            if (isReal) {
                                if (!tierEntryReal || affordableGrossNano === null) return false;
                                return canAffordTier(affordableGrossNano, chipStakeNano, tierEntryReal);
                            }
                            const bal = parseFloat(activeCurrencyBalance);
                            return !isNaN(bal) && amount <= bal;
                        })();
                        const allowed = !restoring && canAffordChip && houseCanCover;
                        const title = !canAffordChip
                            ? t('coinflip.chip.insufficient_for_tier')
                            : (!houseCanCover ? t('coinflip.chip.house_coverage_low') : '');
                        return (
                            <button
                                key={amount}
                                onClick={() => allowed && selectAmount(amount)}
                                disabled={!allowed}
                                title={title}
                                className={`amount-chip py-1.5 px-1 rounded-full text-[12px] font-bold text-center transition-all duration-150 ${isDragonTiger || isGerman || isBrazil ? 'flex-1 min-w-0' : ''} ${betAmount === amount ? 'selected' : ''} ${!allowed ? 'opacity-30 cursor-not-allowed' : ''}`}
                            >
                                {amount}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* CF-V11-CTA-MORPH-V1: stable base summary — the 50:50 trust
                anchor (PL #5875). ALWAYS renders "Bet X → Win 2X TON" regardless
                of selected tier. Tier-specific cost + win moves into the morphing
                FLIP button at commit moment. */}
            {/* CF-DEMO-TIERS-FLIP-FIT-V1: hide the duplicate bet→win summary on the
                variant (the FLIP CTA already shows stake/win) to reclaim vertical
                space so FLIP fits without a pin; default app keeps it unchanged.
                CF-GERMAN-VARIANT-V1: same FLIP-fit reclaim for the german variant. */}
            {!isDragonTiger && !isGerman && !isBrazil && (
                <div className="flex items-center justify-center gap-2 mb-3 py-2 text-center">
                    <span className="text-[13px] font-medium" style={{ color: 'var(--text-secondary)' }}>
                        {t('coinflip.base_summary_bet')}{' '}<span className="font-black" style={{ color: 'var(--text-primary)' }}>{fmtTon(stakeNano)}</span>
                        {' → '}
                        {t('coinflip.base_summary_win')}{' '}<span className="font-black" style={{ color: 'var(--emerald)' }}>{fmtTon(stakeNano * 2n)}</span>
                        {' '}{displayCurrency}
                    </span>
                </div>
            )}

            {/* CF-V11-CTA-MORPH-V1 + CF-DEMO-TIERS-V1: BoostTierSelector shown whenever
                tier_config is present — real money AND demo, so players can try Boost/Mega
                risk-free. Hidden only when tier_config is absent (disconnected/unauthed),
                where demo stays Standard. */}
            {(isReal || inDemoMode) && tierConfig && (
                <BoostTierSelector
                    selectedTier={selectedTier}
                    onChange={setSelectedTier}
                    tierConfig={tierConfig}
                />
            )}

            {/* CF-V11-CTA-MORPH-V1: morphing FLIP button (PL #5875 "genius").
                Real mode: deriveCtaCopy renders tier-specific cost + win at
                commit moment (Standard "🪙 FLIP X TON" / Boost "⚡️ Boost +fee →
                Win win TON" / Mega "⚡️⚡️ Mega ..."). Demo mode: simple form.
                deriveCtaCopy throws if tierConfig is null — gated by isReal &&
                tierConfig present (mirrors BoostTierSelector visibility). */}
            <button
                onClick={handleFlip}
                disabled={!choice || !canSubmitBet}
                className="btn-primary w-full rounded-2xl text-base font-black tracking-wider"
                style={{
                    paddingTop: '18px',
                    paddingBottom: '18px',
                    fontSize: '16px',
                    letterSpacing: '0.08em',
                    boxShadow: choice ? '0 8px 32px rgba(244, 213, 111, 0.3), 0 0 0 1px rgba(244, 213, 111, 0.1)' : 'none',
                }}
            >
                {restoring
                    ? t('coinflip.connecting')
                    : !choice
                        ? t('coinflip.flip_btn_pick_side')
                        : isReal && tierConfig
                            ? deriveCtaCopy(stakeNano, selectedTier, tierConfig)
                            : inDemoMode && tierConfig && selectedTier !== 'standard'
                                ? deriveCtaCopy(stakeNano, selectedTier, tierConfig)
                                : t('coinflip.flip_btn_demo', { stake: fmtTon(stakeNano), currency: displayCurrency })}
            </button>

            {/* Disabled-state explainer — gross-cost-aware copy when tier fee
                pushes a stake-affordable bet over the balance line. */}
            {choice && !canSubmitBet && (
                <p className="text-[10px] mt-2 text-center" style={{ color: 'var(--text-muted)' }}>
                    {isReal && tierEntryReal && affordableGrossNano !== null && grossNanoReal > affordableGrossNano
                        ? (selectedTier === 'standard'
                            ? t('coinflip.flip_disabled.insufficient')
                            : t('coinflip.flip_disabled.tier_fee_exceeds', { gross: fmtTon(grossNanoReal) }))
                        : inDemoMode && tierEntryDemo && selectedTier !== 'standard' && grossNanoDemo > BigInt(Math.round(balanceNumeric * 1e9))
                            ? t('coinflip.flip_disabled.tier_fee_exceeds', { gross: fmtTon(grossNanoDemo) })
                        : betAmount > balanceNumeric
                            ? t('coinflip.flip_disabled.insufficient')
                            : t('coinflip.flip_disabled.limit_unavailable')}
                </p>
            )}
            </>
            )}

            {/* Provably Fair info button */}
            <button
                onClick={() => { setShowInfo(true); sound.click(); haptic('light'); }}
                className="flex items-center justify-center gap-1.5 mt-3 w-full py-1 transition-all active:scale-[0.98]"
            >
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                     style={{ color: 'var(--text-muted)' }}>
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    <path d="M9 12l2 2 4-4" />
                </svg>
                <span className="text-[9px] font-medium" style={{ color: 'var(--text-muted)' }}>
                    {(isConnected || restoring)
                        ? t('coinflip.pf_button_verifiable')
                        : t('coinflip.pf_button_free_demo')}
                </span>
                <span className="text-[9px] font-bold px-1 py-0.5 rounded-full"
                      style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--text-muted)' }}>
                    ⓘ
                </span>
            </button>

            {/* CF-BONUS-WOW-V1: the ENTRY takeover — full-screen, shown once per CLIENT
                (a per-client display-frequency localStorage flag inside the component —
                never a durable player-identity guarantee), server-gated on the same
                none+offer_available predicate. CTA is action-honest per connection
                state, exactly like the inline banner mounts. z-[90] keeps it beneath
                the deposit modal it can open. */}
            {!restoring && (
                <BonusWelcome
                    view={bonusOfferView}
                    primaryLabel={isConnected ? t('coinflip.deposit_ton_btn') : t('coinflip.connect_wallet_deposit_btn')}
                    onPrimary={isConnected ? () => setShowDeposit(true) : onConnect}
                    termsHref="/bonus-terms"
                />
            )}

            {/* DEPOSIT MODAL */}
            {showDeposit && isConnected && (
                <DepositModal
                    bonusView={bonusView}
                    apiBase={apiBase}
                    walletAddress={walletAddress || ''}
                    tonConnectUI={tonConnectUI}
                    onClose={() => setShowDeposit(false)}
                    onDeposited={() => {
                        setShowDeposit(false);
                        onDepositRefresh?.();
                        showToast(t('coinflip.toast.deposit_confirmed'));
                    }}
                />
            )}

            {/* INFO MODAL — custodial trust copy */}
            {showInfo && (
                <div className="fixed inset-0 z-[100] flex items-end justify-center"
                     style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
                     onClick={() => setShowInfo(false)}>
                    <div className="w-full max-w-lg rounded-t-3xl overflow-y-auto"
                         style={{ background: 'var(--bg-card)', maxHeight: '85vh' }}
                         onClick={e => e.stopPropagation()}>
                        <div className="flex justify-center pt-3 pb-2">
                            <div className="w-10 h-1 rounded-full" style={{ background: 'var(--border-subtle)' }} />
                        </div>

                        <div className="px-5 pb-8">
                            <div className="text-center mb-6">
                                <span className="text-4xl">🪙</span>
                                <h2 className="text-xl font-black mt-2">{t('coinflip.info.title')}</h2>
                                <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                                    {t('coinflip.info.subtitle')}
                                </p>
                            </div>

                            <div className="space-y-3 mb-5">
                                <div className="glass-gold rounded-2xl p-4">
                                    <div className="flex gap-3">
                                        <span className="text-2xl flex-shrink-0">🔒</span>
                                        <div>
                                            <p className="text-[13px] font-bold">{t('coinflip.info.custody_title')}</p>
                                            <p className="text-[11px] leading-relaxed mt-1" style={{ color: 'var(--text-secondary)' }}>
                                                {t('coinflip.info.custody_body')}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="glass-gold rounded-2xl p-4">
                                    <div className="flex gap-3">
                                        <span className="text-2xl flex-shrink-0">🎲</span>
                                        <div>
                                            <p className="text-[13px] font-bold">{t('coinflip.info.locked_title')}</p>
                                            <p className="text-[11px] leading-relaxed mt-1" style={{ color: 'var(--text-secondary)' }}>
                                                {t('coinflip.info.locked_body')}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="glass-gold rounded-2xl p-4">
                                    <div className="flex gap-3">
                                        <img src={cfDiamondMark} alt="" className="w-7 h-7 flex-shrink-0" />
                                        <div>
                                            <p className="text-[13px] font-bold">{t('coinflip.info.withdraw_title')}</p>
                                            <p className="text-[11px] leading-relaxed mt-1" style={{ color: 'var(--text-secondary)' }}>
                                                {t('coinflip.info.withdraw_body')}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="glass rounded-2xl p-4 mb-5">
                                <p className="text-[10px] font-semibold tracking-[0.15em] uppercase mb-2"
                                   style={{ color: 'var(--emerald)' }}>
                                    {t('coinflip.info.verify_dont_trust_title')}
                                </p>
                                <p className="text-[11px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                                    {t('coinflip.info.verify_dont_trust_body')}
                                </p>
                            </div>

                            <div className="glass rounded-2xl p-4 mb-5">
                                <p className="text-[13px] font-bold mb-1">{t('coinflip.info.why_title')}</p>
                                <p className="text-[11px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                                    {t('coinflip.info.why_body')}
                                </p>
                            </div>

                            <button
                                onClick={() => setShowInfo(false)}
                                className="btn-primary w-full py-3.5 rounded-2xl text-sm font-bold"
                            >
                                {t('coinflip.info.dismiss_btn')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
